import { authManager } from "@/lib/managers/auth-manager";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { AuthAction, usePermissionContext } from "@/react/providers/permission-context-provider";
import {
   Assignment2Store,
   Assignment2Store as AssignmentStore,
} from "@/stores/assignment-2-store.core";
import { SettingsStore } from "@/stores/settings-store.core";
import type { CalendarModel, Gantt, TaskModel } from "@bryntum/gantt-thin";
import { CalendarManagerStore } from "@bryntum/gantt-thin";
import { TaskStore } from "@bryntum/gantt-thin";
import { BryntumGantt, BryntumGanttProjectModel } from "@bryntum/gantt-react-thin";
// Bryntum v7 CSS
import "./bryntum-common.css";
import "@bryntum/gantt/gantt.css";
import "@bryntum/gantt/stockholm-light.css";
import { getAttachedDate, getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import { CaretsInVertical, CaretsOutVertical, Excavator } from "@procore/core-icons";
import {
   Box,
   Link,
   P,
   Spinner,
   SplitViewCard,
   ToolLandingPage,
   useI18nContext,
} from "@procore/core-react";
import { timeFormat } from "d3-time-format";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { renderToString } from "react-dom/server";
import { renderTimeOffTooltip } from "./shared/components/time-off-tooltip";
import TearsheetProvider from "../tearsheets";
import { DAYS, getCalendarsGanttData } from "./gantt-calendar";
import type { ExtractGanttProp } from "./gantt-config";
import { initGanttConfig, ProcoreTaskModel, projectConfig } from "./gantt-config";
import {
   getGanttConfigurePanelValues,
   updateGanttConfigurePanelLocalStorage,
} from "./gantt-config-panel";
import { GanttControlPanel } from "./gantt-control-panel";
import {
   DATE_RANGE_STORAGE_KEY,
   defaultPreset,
   type DateRangeDays,
} from "./gantt-date-range-constants";
import {
   getSavedDateRange,
   computeInitialDates,
   computeRangeBounds,
   computeRangeBoundsWithClearedTime,
   computeDefaultRange,
   getMainUnitInMs,
   getPresetForDays,
   getSchedulerInitialDateRange,
} from "./gantt-date-range-utils";
import { MyXlsProvider } from "./gantt-export-provider";
import { GanttFilterPanel } from "./gantt-filter-panel";
import { showBryntumModal, BryntumModalType } from "../common/bryntum/bryntum-modals";
import type { GanttTearsheetRef } from "./gantt-tearsheets";
import {
   GanttCreateAssignmentTearsheet,
   GanttCreateRequestTearsheet,
   GanttEditAssignmentTearsheet,
   GanttEditRequestTearsheet,
   GanttPersonDetailsTearsheet,
   GanttProjectDetailsTearsheet,
   GanttSendOneOffAssignmentAlertsTearsheet,
} from "./gantt-tearsheets";
import { GanttTotalsService } from "./gantt-totals";
import {
   getFilterFormatted,
   subtractOneMinuteFromTaskEndDate,
   getTimeOffReasonDisplay,
} from "./gantt-utils";

import type { CompanyEntityOptionsResponse } from "@/react/prop-types";
import { ResourceType } from "@/react/prop-types";
import { GanttStore } from "@/stores/gantt-custom-filter-store.core";
import LaunchDarklyClient from "@laborchart-modules/launch-darkly-browser";
import { useGetSavedView } from "../common/queries/queries";
import { ResourceSchedule } from "./resource-schedule/resource-schedule";
import "./gantt-container.css";
import {
   clearChanges,
   createProjectTask,
   getColorShades,
   getInitialFilters,
   getSelectedBarColorHexValues,
   getTextColorForBackground,
   groupTasks,
   resumeAutoSync,
   sortPriorityLevels,
   suspendAutoSync,
   transformPeopleGanttData,
} from "./gantt-utils";
import type {
   ganttFilterType,
   GanttOptions,
   GanttProject,
   RawGanttData,
   TotalByDay,
} from "./prop-types";
import { GanttViewMode } from "./prop-types";
import {
   REQUEST_AND_ASSIGNMENT_TASK_TYPES,
   AssignmentBarColors,
   TaskType,
   TotalUnitType,
   REQUEST_TASK_TYPES,
   ASSIGNMENT_TASK_TYPES,
   EQUIPMENT_TASK_TYPES,
} from "./prop-types";
import { handleBeforeSort, onGridRowBeforeDropFinalize } from "./gantt-event-handlers";
import { taskRenderer } from "./gantt-renderers";
import type { ViewPreset } from "@bryntum/scheduler-thin";
import { PresetManager } from "@bryntum/scheduler-thin";
import type { Column, ColumnStore } from "@bryntum/grid-thin";
import type { MenuItemConfig, Panel } from "@bryntum/core-thin";
// Importing Bryntum's DateHelper as BryntumDateHelper to avoid confusion with LocaleManager.locale.DateHelper
import {
   DateHelper as BryntumDateHelper,
   Model,
   StringHelper,
   LocaleManager,
} from "@bryntum/core-thin";
import { EventSegmentModel } from "@bryntum/schedulerpro-thin";
import { GanttTooltipComponent } from "./gantt-tooltip-component";
import { useGanttInfiniteScroll } from "./hooks/useGanttInfiniteScroll";
import type { PeopleGanttOptions } from "@laborchart-modules/lc-core-api/dist/api/assignments/get-people-gantt";
import { BryntumLocaleProvider } from "../../providers/BryntumLocaleProvider";
import { CurrencyFormatter, NumberFormatter } from "@procore/globalization-toolkit";
import { handleUnassignTask } from "./gantt-unassign-handler";
import type { UnassignTaskData } from "./gantt-unassign-handler";
import type { BryntumSchedulerPro } from "@bryntum/schedulerpro-react-thin";
import {
   buildCommonConfigurePanelItems,
   buildConfigurePanelStrip,
} from "./gantt-config-panel-strips";

export const GanttContainer = ({ router }: { router: any }) => {
   return (
      <BryntumLocaleProvider>
         <TearsheetContainer router={router} />
      </BryntumLocaleProvider>
   );
};

export const TearsheetContainer = ({ router }: { router: any }) => {
   return (
      <TearsheetProvider>
         <Container router={router} />
      </TearsheetProvider>
   );
};

const GANTT_PAGE_SIZE = 40;
const ganttTotalService = new GanttTotalsService();

const GANTT_EXPANDED_PROJECTS_KEY = "gantt-expanded-projects";
const GANTT_EXPANDED_PROJECTS = JSON.parse(
   localStorage.getItem(GANTT_EXPANDED_PROJECTS_KEY) ?? "[]",
);

const fileExportName = `Gantt_${timeFormat("%Y%m%d")(new Date())}`;

// Function to parse "HH:MM:SS" strings into Date objects
export const parseTimeString = (timeString: string): Date => {
   try {
      const [hours, minutes, seconds] = timeString.split(":").map(Number);
      const date = new Date();
      date.setHours(hours, minutes, seconds, 0);
      return date;
   } catch (e) {
      console.error("Error parsing time string:", timeString, e);
      return new Date();
   }
};

/**Bryntum localisation support**/
const defaultTimeFormat = "hh:mm A";
const defaultDateFormat = "MM/DD/YYYY";
const defaultCurrency = "USD";
const defaultLocale = "en-US";

// Date format from Bryntum's current locale
export const getBryntumDateFormat = () => {
   // DateHelper here is from LocaleManager.locale, not the imported BryntumDateHelper
   const { DateHelper } = LocaleManager.locale as any;
   return DateHelper?.parsers?.L || defaultDateFormat;
};
// Localized date formatting
export const formatDateBryntum = (date: Date | string | number) =>
   date ? BryntumDateHelper.format(new Date(date), getBryntumDateFormat()) : "";

// Time format from Bryntum's current locale
export const getBryntumTimeFormat = () => {
   // DateHelper here is from LocaleManager.locale, not the imported BryntumDateHelper
   const { DateHelper } = LocaleManager.locale as any;
   const timeFormat = DateHelper?.parsers?.LT;
   if (timeFormat === "HH:mm A") return defaultTimeFormat; // return default if Bryntum returns this uncommon format (24hr time with AM/PM indicator)
   return timeFormat || defaultTimeFormat;
};

// Localized time formatting
export const formatTimeBryntum = (timeString: string) =>
   timeString ? BryntumDateHelper.format(parseTimeString(timeString), getBryntumTimeFormat()) : "";

export const getMultipliers = (mainUnit: string) => {
   switch (mainUnit.toLowerCase()) {
      case "day":
         return { back: 1, forward: 4 }; // 1 day back, 4 days forward (5 days total)
      case "week":
         return { back: 1, forward: 3 }; // 1 week back, 3 weeks forward (~1 month total)
      case "month":
         return { back: 1, forward: 2 }; // 1 month back, 2 months forward (3 months total)
      case "year":
         return { back: 0.25, forward: 4 }; // 3 months back, 3 years 9 months forward (4 years total)
      default:
         return { back: 0.5, forward: 1 };
   }
};

// Function that returns the percentage allocation of the assignment/request
export const getPercentAllocation = (
   projectId: string,
   id: string,
   ganttData: RawGanttData | undefined,
   ganttInstance: Gantt | undefined,
): number | null => {
   const project = ganttData?.projects.find((project) => project.id === projectId);
   let task =
      project?.assignments.find((assignment) => assignment.id === id) ??
      project?.requests.find((request) => request.id === id) ??
      project?.equipment_assignments.find((equipmentAssignment) => equipmentAssignment.id === id);

   // If not found in ganttData, try to find in Bryntum task store as fallback
   if (!task && ganttInstance) {
      // Access the taskStore from the Bryntum Gantt instance
      const taskRecord = ganttInstance.taskStore.getById(id);
      if (taskRecord) {
         task = {
            id: taskRecord.getData("id"),
            percent_allocated: taskRecord.getData("percentAllocated"),
         } as any;
      }
   }
   return task?.percent_allocated ?? null;
};

export const getDatesRangeString = (
   startDate: Date,
   endDate: Date,
   startTime?: string,
   endTime?: string,
) => {
   return `${formatDateBryntum(startDate)} ${startTime ?? ""} - ${formatDateBryntum(endDate)} ${
      endTime ?? ""
   }`;
};

const Container = ({ router }: { router: any }) => {
   const [numberOptions, setNumberOptions] = useState({
      locale: defaultLocale,
      minimumFractionDigits: 0,
   });
   const [currencyOptions, setCurrencyOptions] = useState({
      locale: defaultLocale,
      currencyIsoCode: defaultCurrency,
   });

   useEffect(() => {
      const { NumberFormat } = LocaleManager.locale as any;
      setNumberOptions({
         locale: NumberFormat?.locale || defaultLocale,
         minimumFractionDigits: 0,
      });
      setCurrencyOptions({
         locale: NumberFormat?.locale || defaultLocale,
         currencyIsoCode: NumberFormat?.currency || defaultCurrency,
      });
   }, [LocaleManager.locale]);

   const numberFormatter = useMemo(() => new NumberFormatter(numberOptions), [numberOptions]);
   const currencyFormatter = useMemo(
      () => new CurrencyFormatter(currencyOptions),
      [currencyOptions],
   );
   // Check search params for viewId and/or subjectName, and load saved view or search for subject
   const url = new URL(window.location.href);
   const searchParams = url.searchParams;
   const viewId = searchParams.get("viewId");
   const subjectName = searchParams.get("subjectName");
   const { data: savedViewData, isLoading: savedViewLoading } = useGetSavedView(viewId);

   const hasPermissionToViewPeopleTimeoff = !!authManager
      .authedUser()
      ?.permissionLevel()
      ?.viewPeopleTimeoff();

   const { groupId } = useGroupContext();
   const I18n = useI18nContext();
   const { checkAuthAction } = usePermissionContext();
   const [ganttData, setGanttData] = useState<RawGanttData>();
   const [customFilterFields, setCustomFilterFields] =
      useState<CompanyEntityOptionsResponse | null>(null);
   const [isMaskOn, setIsMaskOn] = useState<boolean>(true);
   const [expandedTasks, setExpandedTasks] = useState<Set<string>>(
      new Set(GANTT_EXPANDED_PROJECTS),
   );

   const isSchedulerProEnabled = useMemo(
      () => LaunchDarklyClient.getFlagValue("scheduler-pro-resource-view"),
      [],
   );
   const showGanttTotalsConfig = useMemo(
      () => LaunchDarklyClient.getFlagValue("show-gantt-totals-config"),
      [],
   );

   let droppingGridRow = false;

   //#region utility functions
   const expandTask = (id: string) => {
      const updatedExpandedTasks = expandedTasks.add(id);
      setExpandedTasks(new Set([...updatedExpandedTasks]));
      setTimeout(() => {
         localStorage.setItem(
            GANTT_EXPANDED_PROJECTS_KEY,
            JSON.stringify(Array.from(updatedExpandedTasks)),
         );
      }, 10);
   };

   const collapseTask = (id: string) => {
      expandedTasks.delete(id);
      setExpandedTasks(new Set([...expandedTasks]));
      setTimeout(() => {
         localStorage.setItem(
            GANTT_EXPANDED_PROJECTS_KEY,
            JSON.stringify(Array.from(new Set(expandedTasks))),
         );
      }, 10);
   };
   //#endregion

   /* For more information on what is inside of the PresetManager, you can view the file `node_modules/@bryntum/gantt/gantt.module.js`
      and search for line "var PresetManager = class extends PresetStore"

      You can also check the Bryntum docs here for examples on how to use or alter this PresetManager or create custom ViewPreset's:
         - https://bryntum.com/products/gantt/docs/api/Scheduler/preset/PresetManager
         - https://bryntum.com/products/gantt/docs/api/Scheduler/preset/ViewPreset
   */
   const availablePresets = (PresetManager.records.slice(3, 11) as ViewPreset[]).map(
      (preset: ViewPreset) => {
         return {
            id: "my_" + preset.id,
            base: preset.id,
            // mainUnit and defaultSpan are used to dynamically set the viewing range of our gantt
            mainUnit: preset.mainUnit,
            defaultSpan: preset.defaultSpan,
            tickWidth: preset.tickWidth + 30,
            // timeResolution makes drag-and-drop snaps to your unit
            timeResolution: {
               unit: "day",
               increment: 1,
            },
         };
      },
   ) as ViewPreset[];

   const viewPreset =
      availablePresets.find(
         (preset: any) => preset.id === localStorage.getItem("gantt-view-preset"),
      ) ?? (availablePresets.at(-1) as ViewPreset);

   const mainUnitInMs = getMainUnitInMs(viewPreset.mainUnit);

   const mainUnitOffset = mainUnitInMs * (viewPreset.defaultSpan ?? 1);
   const multipliers = getMultipliers(viewPreset.mainUnit);
   const START_DATE = new Date();
   START_DATE.setTime(START_DATE.getTime() - mainUnitOffset * multipliers.back);
   const END_DATE = new Date();
   END_DATE.setTime(END_DATE.getTime() + mainUnitOffset * multipliers.forward);

   const [selectedDateRange, setSelectedDateRange] = useState<DateRangeDays>(getSavedDateRange());

   const { startDate: initialStartDate, endDate: initialEndDate } = computeInitialDates(
      selectedDateRange,
      START_DATE,
      END_DATE,
   );

   const [currentGanttRange, setCurrentGanttRange] = useState({
      startDay: getDetachedDay(initialStartDate),
      endDay: getDetachedDay(initialEndDate),
   });
   const earliestStartDateViewed = useRef<number>(currentGanttRange.startDay);
   const latestEndDateViewed = useRef<number>(currentGanttRange.endDay);
   const startDate = useRef<Date>(getAttachedDate(currentGanttRange.startDay));
   const endDate = useRef<Date>(getAttachedDate(currentGanttRange.endDay));

   const [ganttViewMode, setGanttViewMode] = useState<GanttViewMode>(
      (localStorage.getItem("gantt-view-mode") as GanttViewMode) ?? GanttViewMode.PROJECTS,
   );

   const updateDateRangeState = useCallback((days: DateRangeDays) => {
      setSelectedDateRange(days);
      if (days !== null) {
         localStorage.setItem(DATE_RANGE_STORAGE_KEY, String(days));
      } else {
         localStorage.removeItem(DATE_RANGE_STORAGE_KEY);
      }
   }, []);

   const updateDateRefs = (start: Date, end: Date) => {
      earliestStartDateViewed.current = getDetachedDay(start);
      latestEndDateViewed.current = getDetachedDay(end);
      startDate.current = getAttachedDate(earliestStartDateViewed.current);
      endDate.current = getAttachedDate(latestEndDateViewed.current);
   };

   const resetDateRange = () => {
      updateDateRangeState(null);
      localStorage.setItem("gantt-view-preset", defaultPreset.id);

      if (isSchedulerProView) {
         clearSchedulerDateBounds(defaultPreset.id);
      } else {
         const gantt = ganttRef.current?.instance;
         if (!gantt) return;
         gantt.viewPreset = defaultPreset.id;

         const mainUnitInMs = getMainUnitInMs(defaultPreset.mainUnit);
         const mainUnitOffset = mainUnitInMs * (defaultPreset.defaultSpan ?? 1);
         const multipliers = getMultipliers(defaultPreset.mainUnit);
         const { resetStart, resetEnd } = computeDefaultRange(mainUnitOffset, multipliers);
         updateDateRefs(resetStart, resetEnd);
         gantt.setTimeSpan(startDate.current, endDate.current);
         skip.current = 0;
         fetchData();
      }
   };

   const [paidShiftHours, setPaidShiftHours] = useState(8); // Paid shift is 8 hours by default
   const fetchTimeoutId = useRef<number>(NaN);
   // Checking for saved-view ganttFilter, then check localStorage for last applied ganttFilter, then fallback to default filters
   const savedViewConfig: any = savedViewData?.data?.view_config;
   const [search, setSearch] = useState<string>(
      subjectName ?? savedViewConfig?.search ?? localStorage.getItem("gantt-search") ?? "",
   );
   const prevSearch = useRef<string>(search);
   const ganttFilterSaved: ganttFilterType | null = JSON.parse(
      localStorage.getItem("gantt-filter")!,
   );
   const [ganttFilter, setGanttFilter] = useState<ganttFilterType>(
      getInitialFilters(ganttFilterSaved),
   );
   const [filterPanelOpen, setFilterPanelOpen] = useState<boolean>(false);
   const canViewProject = checkAuthAction(AuthAction.VIEW_PROJECT);
   const canViewPeople = checkAuthAction(AuthAction.VIEW_PEOPLE);
   const canViewCostingSettings = checkAuthAction(AuthAction.VIEW_COSTING_SETTINGS);
   const projectDetailsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const createAssignmentTearsheetRef = useRef<GanttTearsheetRef>(null);
   const sendAssignmentAlertsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const createRequestTearsheetRef = useRef<GanttTearsheetRef>(null);
   const editAssignmentTearsheetRef = useRef<GanttTearsheetRef>(null);
   const editRequestTearsheetRef = useRef<GanttTearsheetRef>(null);
   const personDetailsTearsheetRef = useRef<GanttTearsheetRef>(null);
   const skip = useRef(0);
   const sortDirection = useRef<"asc" | "desc">("asc");
   const projectSort = useRef<"name" | "earliest-start-date" | "earliest-end-date">("name");
   const hasAllRecords = useRef(false);
   const isLoadingMoreRef = useRef(false);
   // The BryntumGantt is a wrapper for the actually underlying gantt instance. If you want to,
   // you can use ganttRef.current.instance to access the actual underlying gantt instance.
   const ganttRef = useRef<BryntumGantt>(null);
   const schedulerRef = useRef<BryntumSchedulerPro>(null);
   const projectRef = useRef<BryntumGanttProjectModel>(null);
   // EXAMPLES of how you can access different internal stores of the ProjectModel (https://bryntum.com/products/gantt/docs/guide/Gantt/data/project_data#updating-the-project-data):
   // ganttRef.current?.instance.project.changes
   // ganttRef.current?.instance.project.taskStore.changes
   // ganttRef.current?.instance.project.timeRangeStore.changes
   // ganttRef.current?.instance.widgetMap.right.toggleCollapsed()
   // ganttRef.current?.instance.element.style.setProperty("--row-height", `${getGanttConfigurePanelValues().rowHeight}px`);

   // Using a ref for this so that it's value persists across component re-renders and does not cause a re-render when it itself is updated
   const isInitialLoadComplete = useRef<boolean>(false);
   // Track when we're setting up from saved view to prevent ganttFilter effect from double-fetching
   const isSettingUpSavedView = useRef<boolean>(false);

   // Add a ref to store scroll position
   const scrollPositionRef = useRef<number>(0);

   // Determine if we're in Scheduler Pro view
   const isSchedulerProView: boolean =
      isSchedulerProEnabled && ganttViewMode === GanttViewMode.RESOURCES;

   const clearSchedulerDateBounds = (presetId: string) => {
      const scheduler = schedulerRef.current?.instance as any;
      if (!scheduler) return;
      scheduler.minDate = null;
      scheduler.maxDate = null;
      scheduler.viewPreset = presetId;
   };

   const applySchedulerDateRange = (days: number, anchor?: Date) => {
      const { rangeStart, rangeEnd } = computeRangeBoundsWithClearedTime(days, anchor);
      const scheduler = schedulerRef.current?.instance as any;
      if (!scheduler) return;
      scheduler.minDate = null;
      scheduler.maxDate = null;
      scheduler.zoomTo({ preset: getPresetForDays(days), centerDate: rangeStart });
      scheduler.minDate = rangeStart;
      scheduler.maxDate = rangeEnd;
      scheduler.scrollToDate(rangeStart, { block: "start" });
   };

   const applyDateRange = (days: DateRangeDays, anchor?: Date) => {
      if (days === null) {
         resetDateRange();
         return;
      }

      updateDateRangeState(days);

      if (isSchedulerProView) {
         applySchedulerDateRange(days, anchor);
      } else {
         const gantt = ganttRef.current?.instance;
         if (!gantt) return;
         const { rangeStart, rangeEnd } = computeRangeBounds(days, anchor);
         updateDateRefs(new Date(rangeStart), new Date(rangeEnd));
         gantt.viewPreset = getPresetForDays(days);
         gantt.setTimeSpan(startDate.current, endDate.current);
      }
   };

   // Always fetch this supporting data on initial page load (empty dependency array so only gets executed once)
   useEffect(() => {
      const fetchCostingData = async () => {
         const costingInfo = await SettingsStore.getCostingInfo()?.payload;
         setPaidShiftHours(costingInfo?.data?.paid_shift_hours ?? 8);
      };

      const fetchCustomFieldsFilters = async () => {
         const response = await GanttStore.getGanttCustomFilter(groupId).payload;
         const customFilterFields = response.data;
         setCustomFilterFields(customFilterFields);
      };

      try {
         if (canViewCostingSettings) {
            fetchCostingData();
         }
         fetchCustomFieldsFilters();
      } catch (e) {
         console.error(e);
      }
   }, []);

   // As soon as potential saved-view data loads, fetch Gantt data
   useEffect(() => {
      // Guard must be OUTSIDE async function to run synchronously and block StrictMode double-invoke
      if (savedViewLoading) return;
      if (isLoadingMoreRef.current) return;
      // Set immediately to block any concurrent/duplicate calls (before async work starts)
      isLoadingMoreRef.current = true;

      const fetchDataAsync = async () => {
         if (savedViewData) {
            // Mark that we're setting up from saved view - this prevents the ganttFilter effect
            // from triggering a duplicate fetch when we call setGanttFilter below
            isSettingUpSavedView.current = true;
            // Update the Gantt UI to reflect the saved-view that we're loading
            const ganttViewConfig = savedViewData.data?.view_config as any;
            setGanttFilter(ganttViewConfig.ganttFilter);
            setCustomFilterFields(ganttViewConfig.customFilterFields);
            setGanttViewMode(ganttViewConfig.ganttViewMode);
            setSearch(ganttViewConfig.search);
            skip.current = 0;

            const savedRange = ganttViewConfig.selectedDateRange ?? null;
            updateDateRangeState(savedRange);

            if (savedRange !== null) {
               localStorage.setItem("gantt-view-preset", getPresetForDays(savedRange));
            }
         }

         await fetchData({
            savedViewConfig,
         });
         // Set immediately so infinite scroll can work after initial data renders
         isInitialLoadComplete.current = true;
         // Allow infinite scroll after a brief delay to let the DOM settle
         setTimeout(() => {
            isLoadingMoreRef.current = false;
            isSettingUpSavedView.current = false;
         }, 500);
      };

      fetchDataAsync();
   }, [savedViewLoading]);

   // Saved view date range restore — Gantt only (SchedulerPro is handled by the effect below)
   useEffect(() => {
      if (!isInitialLoadComplete.current || !savedViewData || selectedDateRange === null) return;
      if (isSchedulerProView) return;
      if (!ganttRef.current?.instance) return;

      applyDateRange(selectedDateRange);
   }, [
      ganttRef.current?.instance,
      isInitialLoadComplete.current,
      savedViewData,
      selectedDateRange,
      isSchedulerProView,
   ]);

   // Apply date range to SchedulerPro — handles view mode switch, page reload, and saved view restore
   useEffect(() => {
      if (!isSchedulerProView || selectedDateRange === null) return;
      applySchedulerDateRange(selectedDateRange);
   }, [isSchedulerProView, schedulerRef.current?.instance]);

   useEffect(() => {
      // Skip if initial load not complete or if we're still loading
      if (!isInitialLoadComplete.current) return;
      if (isLoadingMoreRef.current) return;
      // Skip if we're in the middle of saved view setup (filter was set by savedViewLoading effect)
      if (isSettingUpSavedView.current) return;

      skip.current = 0;
      scrollPositionRef.current = 0;
      localStorage.setItem("gantt-filter", JSON.stringify(ganttFilter));
      // On a manual filter change,remove viewId param from URL so that the last opened saved view is not reloaded on next page refresh
      const url = new URL(window.location.href);
      if (url.searchParams.has("viewId")) {
         url.searchParams.delete("viewId");
         window.history.replaceState({}, "", url.toString());
      }

      fetchData();
   }, [ganttFilter, groupId]);

   // This effect is used to update the customFieldsFilters in the ganttFilter state when the custom field settings are updated
   useEffect(() => {
      if (customFilterFields) {
         const updatedFilters = Object.entries(ganttFilter.customFieldsFilters).reduce(
            (acc: any, [key, value]) => {
               const matchingFilter = customFilterFields.projects_custom_field_filters.find(
                  (filter: any) => filter.id === key,
               );

               if (matchingFilter) {
                  acc[key] = value; // Retain the value if a match is found
               }

               return acc;
            },
            {},
         );
         localStorage.setItem(
            "gantt-filter",
            JSON.stringify({
               ...ganttFilter,
               customFieldsFilters: updatedFilters,
            }),
         );
      }
   }, [customFilterFields]);

   useEffect(() => {
      if (!isInitialLoadComplete.current) return;
      // Making sure we don't call fetchData on the for this effect on initial page load. We only want to execute this
      // after the user themselves updates the input value.
      if (search != prevSearch.current) {
         prevSearch.current = search;
         localStorage.setItem("gantt-search", search ?? "");
         skip.current = 0;
         fetchData();
      }
   }, [search]);

   useEffect(() => {
      if (!isInitialLoadComplete.current) return;
      localStorage.setItem("gantt-view-mode", ganttViewMode);
      router.navigate(null, `/groups/${groupId}/new-gantt`, true);
   }, [ganttViewMode]);

   useEffect(() => {
      if (!isInitialLoadComplete.current) return;

      clearTimeout(fetchTimeoutId.current);

      // When a date range is active, refs are already set by applyDateRange — skip hasViewRangeExtended
      // SchedulerPro manages its own data via Bryntum transport, so skip fetchData for it
      if (selectedDateRange !== null) {
         if (!isSchedulerProView) {
            fetchTimeoutId.current = window.setTimeout(() => {
               skip.current = 0;
               fetchData();
            }, 500);
         }
         return;
      }

      const currentStartDate = currentGanttRange.startDay;
      const currentEndDate = currentGanttRange.endDay;

      const hasViewRangeExtended = () => {
         // If we've already zoomed out past the starting or ending point of the current range then there's no need to fetch
         // more data because we've already loaded a larger set of data which includes the current range.
         let rangeViewExtended = false;

         if (currentStartDate < earliestStartDateViewed.current) {
            rangeViewExtended = true;
            earliestStartDateViewed.current = currentStartDate;
            startDate.current = new Date(getAttachedDate(currentStartDate));
         }
         if (currentEndDate > latestEndDateViewed.current) {
            rangeViewExtended = true;
            latestEndDateViewed.current = currentEndDate;
            endDate.current = new Date(getAttachedDate(currentEndDate));
         }

         return rangeViewExtended;
      };

      // Wait until we go 500ms without receiving another zoom event to check if we need to fetch more data (debounced for performance)
      fetchTimeoutId.current = window.setTimeout(() => {
         if (hasViewRangeExtended()) {
            skip.current = 0;
            fetchData();
         }
      }, 500);
   }, [currentGanttRange]);

   // When the view mode is change triggers this function
   // to hide/show columns and allow grouping,
   useEffect(() => {
      const cols = ganttRef.current?.instance.columns as ColumnStore;
      if (!cols) return;

      let columnsToHide: string[] = [];
      let columnsToShow: string[] = [];
      if (ganttViewMode === GanttViewMode.PROJECTS) {
         columnsToHide = ["resourceId"];
         columnsToShow = ["name", "jobTitleName", "startDate", "endDate"];
      } else {
         columnsToHide = ["name", "jobTitleName", "startDate", "endDate"];
         columnsToShow = ["resourceId"];
      }

      // First we trigger the show else the sidebar will reduce to 0
      columnsToShow.forEach((field) =>
         cols.forEach((col: Column) => {
            if (col.field === field) col.show();
         }),
      );
      columnsToHide.forEach((field) =>
         cols.forEach((col: Column) => {
            if (col.field === field) col.hide();
         }),
      );

      if (ganttViewMode === GanttViewMode.RESOURCES) {
         // These tasks are not relevant to the resource view, and cannot be treeGrouped by resourceId.
         const excludedTaskTypes = new Set([
            TaskType.PROJECT,
            TaskType.CATEGORY,
            TaskType.SUBCATEGORY,
         ]);
         ganttRef.current?.instance.taskStore.filter((task: any) => {
            const taskType = task.getData("type");
            return !excludedTaskTypes.has(taskType);
         });
      }
   }, [ganttViewMode, ganttRef.current?.instance]);

   /**
    * Fetches Gantt data with options to apply filters and search parameters.
    *
    * @param {Object} options - The options for fetching Gantt data.
    * @param {ganttFilterType} [options.ganttFilters=ganttFilter] - The filters to apply when fetching Gantt data.
    *
    * @returns {Promise<void>} - A promise that resolves when the data fetching is complete.
    */
   const fetchData = async (
      { savedViewConfig }: { savedViewConfig?: any } = {
         savedViewConfig: null,
      },
   ) => {
      let params: GanttOptions | PeopleGanttOptions = {
         skip: skip.current,
         limit: GANTT_PAGE_SIZE,
         projectSort: "name",
         group_id: groupId,
         startDay: earliestStartDateViewed.current,
         endDay: latestEndDateViewed.current,
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         sortDirection: sortDirection.current,
         isLegacyGantt: false,
      };

      if (ganttRef.current?.instance) {
         ganttRef.current.instance.mask({
            cls: "gantt-loading-mask",
            html: renderToString(
               <div
                  style={{
                     height: "100%",
                     width: "100%",
                     display: "grid",
                     placeItems: "center",
                  }}
               >
                  <Spinner loading={true} />
               </div>,
            ),
         });
      }
      setIsMaskOn(true);

      const appliedFilters = getFilterFormatted(
         savedViewConfig?.ganttFilter ?? ganttFilter,
         ganttViewMode,
      );
      params.filters = appliedFilters;
      params.search = savedViewConfig?.search ?? search;

      if (ganttViewMode === GanttViewMode.PROJECTS) {
         const rawGanttData = await AssignmentStore.getRawProjectsGanttData(params);
         if (rawGanttData.projects.length < GANTT_PAGE_SIZE) {
            hasAllRecords.current = true;
         } else {
            hasAllRecords.current = false;
         }

         if (skip.current === 0) {
            // Reset data and scroll position on filter/search changes
            ganttRef.current?.instance.scrollToTop();
            setGanttData(rawGanttData);
         } else {
            // Concatenate new data for infinite scroll with deduplication
            ganttRef.current?.instance.storeScroll();
            setGanttData((prevData) => {
               if (!prevData) return rawGanttData;

               // Create Sets of existing IDs for efficient lookup
               const existingJobTitleIds = new Set(prevData.jobTitles.map((jt) => jt.id));
               const existingPeopleIds = new Set(prevData.people.map((p) => p.id));
               const existingEquipmentIds = new Set(prevData.equipment.map((e) => e.id));
               const existingProjectIds = new Set(prevData.projects.map((p) => p.id));

               // Filter out duplicates from new data
               const newJobTitles = rawGanttData.jobTitles.filter(
                  (jt: any) => !existingJobTitleIds.has(jt.id),
               );
               const newPeople = rawGanttData.people.filter(
                  (p: any) => !existingPeopleIds.has(p.id),
               );
               const newEquipment = rawGanttData.equipment.filter(
                  (e: any) => !existingEquipmentIds.has(e.id),
               );
               const newProjects = rawGanttData.projects.filter(
                  (p: any) => !existingProjectIds.has(p.id),
               );

               return {
                  jobTitles: [...prevData.jobTitles, ...newJobTitles],
                  people: [...prevData.people, ...newPeople],
                  equipment: [...prevData.equipment, ...newEquipment],
                  projects: [...prevData.projects, ...newProjects],
               };
            });
         }
      } else {
         params = {
            ...params,
            sort: "name",
         };

         const response = await AssignmentStore.getRawPeopleGanttData(params as PeopleGanttOptions);
         const rawGanttData = transformPeopleGanttData(response.data ?? []);

         if (response.data.length < GANTT_PAGE_SIZE) {
            hasAllRecords.current = true;
         } else {
            hasAllRecords.current = false;
         }
         if (skip.current === 0) {
            setGanttData(rawGanttData);
         } else {
            // Concatenate new data for infinite scroll with deduplication
            ganttRef.current?.instance.storeScroll();
            setGanttData((prevData) => {
               if (!prevData) return rawGanttData;

               // Create Sets of existing IDs for efficient lookup
               const existingJobTitleIds = new Set(prevData.jobTitles.map((jt) => jt.id));
               const existingPeopleIds = new Set(prevData.people.map((p) => p.id));
               const existingEquipmentIds = new Set(prevData.equipment.map((e) => e.id));
               const existingProjectIds = new Set(prevData.projects.map((p) => p.id));

               // Filter out duplicates from new data
               const newJobTitles = rawGanttData.jobTitles.filter(
                  (jt: any) => !existingJobTitleIds.has(jt.id),
               );
               const newPeople = rawGanttData.people.filter(
                  (p: any) => !existingPeopleIds.has(p.id),
               );
               const newEquipment = rawGanttData.equipment.filter(
                  (e: any) => !existingEquipmentIds.has(e.id),
               );
               const newProjects = rawGanttData.projects.filter(
                  (p: any) => !existingProjectIds.has(p.id),
               );

               return {
                  jobTitles: [...prevData.jobTitles, ...newJobTitles],
                  people: [...prevData.people, ...newPeople],
                  equipment: [...prevData.equipment, ...newEquipment],
                  projects: [...prevData.projects, ...newProjects],
               };
            });
         }
      }

      if (ganttRef.current?.instance) {
         ganttRef.current.instance.unmask();
      }
      setIsMaskOn(false);
   };

   (window as any).ganttRef = ganttRef;

   const getTaskTimeAllocation = (
      startTime: any,
      endTime: any,
      projectId: string,
      id: string,
   ): string => {
      if (!startTime || !endTime) {
         return `${getPercentAllocation(projectId, id, ganttData, ganttRef.current?.instance)}`;
      }
      return `${startTime} - ${endTime}`;
   };

   // Function that returns custom tooltip template for tasks
   const tooltipTemplateCallback = (taskRecord: any, startDate: Date, endDate: Date): string => {
      const id = taskRecord.getData("id");
      const startTime = taskRecord.getData("startTime");
      const endTime = taskRecord.getData("endTime");
      const projectId = taskRecord.getData("projectId");
      const type = taskRecord.getData("type");

      let isAssignmentOrRequest = false,
         taskTimeAllocation = "";

      if (REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(type)) {
         isAssignmentOrRequest = true;
         taskTimeAllocation = getTaskTimeAllocation(startTime, endTime, projectId, id);
      }

      return `<div>
      <div style="text-align: center">${StringHelper.encodeHtml(
         getDatesRangeString(startDate, endDate),
      )}</div>
      ${
         isAssignmentOrRequest
            ? `<div style="text-align: center">
            ${StringHelper.encodeHtml(
               `${startTime && endTime ? taskTimeAllocation : `${taskTimeAllocation}%`}`,
            )}
         </div>`
            : ""
      }
      </div>`;
   };

   /**
    * This function returns a pill displaying the number of open requests that appear on the project's bar
    */
   const requestsNumberPill = (requestsNumber: number): string => {
      if (!requestsNumber) return "";

      const pillsHeader = `${requestsNumber} open request${requestsNumber > 1 ? "s" : ""}`;

      return `<div class='ganttProjectRequestsPill'>${pillsHeader}</div>`;
   };

   const hideWeekends = (gantt: Gantt, value: boolean) => {
      const { timeAxis } = gantt;

      gantt.element.classList.toggle("b-hide-weekends", value);

      gantt.runWithTransition(() => {
         if (value) {
            timeAxis.filterBy(
               (tick: any) =>
                  timeAxis.unit !== "day" ||
                  (tick.startDate.getDay() !== 6 && tick.startDate.getDay() !== 0),
            );
         } else {
            timeAxis.clearFilters();
         }
      });
   };

   const addCalendarIfNotExists = (calendar: CalendarModel) => {
      const manager = (ganttRef.current?.instance as any)
         .calendarManagerStore as CalendarManagerStore;

      if (!manager.getById(calendar.id)) {
         manager.add(calendar);
         manager.commit();
      }
   };

   const taskSchedulingConflictHandler = async (
      context: {
         async: boolean;
         // eslint-disable-next-line @typescript-eslint/ban-types
         finalize: Function;
      },
      taskRecords: TaskModel[],
   ) => {
      context.async = true;

      const requestPayload = taskRecords
         .flatMap((model) => [model, ...model.allChildren])
         .filter((model) => model.getData("type") === "assignment")
         .map((model) => {
            return {
               // The assignment ID
               id: String(model.id),
               // Although we can use the assignment ID to lookup this resourceId value on the backend, we're choosing to pass it in
               // explicitly since we have it available here. This is a tradeoff.
               //
               // On one hand, requiring this additional parameter in the schedulingConflictCheck payload gives the dev one more
               // thing to think about when using the endpoint, and also creates surface area for a bug to occur if the dev passes
               // a resource ID here that doesn't actually match the assignment's real resource ID.
               //
               // The upside is however, that we save the backend from having to do an additional lookup to find the resource ID.
               // Intuition tells me it might be better to eliminate this parameter to simplify the endpoint and just do that lookup
               // on the backend. The amount of overhead added from a simple `pg.select("resource_id").from("assignments").where("id", id)`
               // lookup should be negligible. But since I'm overly optimizing here for backend latency/performance, and this is the only
               // place on the frontend that we anticipate using this endpoint, I'm just going to leave it in.
               resourceId: model.getData("resourceId"),
               startDate: model.getData("startDate"),
               endDate: model.getData("endDate"),
            };
         });

      const conflictsByResource = await Assignment2Store.schedulingConflictCheck(requestPayload)
         .payload;

      // When we check server-side for conflicts, we are checking assignment start/end dates as they are saved in the database right now.
      // However, it is possible that one of those returned assignments we're "conflicting" with is also in the request payload and staged
      // to potentially have it's own dates changed as well. Because of this edge-case, we need to cross-examine the returned server-side
      // conflicts with whatever pending date changes we have here on the client-side to double-verify that all conflicts are fully valid.
      // We may discover that some of the conflicts will no longer be valid because we're actually about to move the assignment that a
      // change would have been conflicting with, but in it's new pending position it is no longer conflicting with that change.
      //
      // TLDR: Double-verify on the client-side that all conflicts would still be valid after pending date changes!
      // See slack post for demonstration: https://procoretech.slack.com/archives/C06TXQUUM8T/p1746711445311149
      const noLongerConflicting: { [resourceId: string]: string[] } = {};
      for (const [resourceId, conflicts] of Object.entries(conflictsByResource)) {
         for (const conflict of conflicts) {
            const assignmentChangesForResource = requestPayload.filter(
               (assignment) => assignment.resourceId === resourceId,
            );

            // If the assignment we are conflicting with ALSO has it's dates changing, then we need to proceed double verify here
            const conflictingAssignmentAfterPendingChange = assignmentChangesForResource.find(
               (changingAssignment) => changingAssignment.id === conflict.id,
            );
            if (!conflictingAssignmentAfterPendingChange) continue;

            let isStillConflicting = false;

            for (const change of assignmentChangesForResource) {
               // Skip the assignment we are re-checking because it can't conflict with itself
               if (conflictingAssignmentAfterPendingChange.id === change.id) continue;

               if (
                  conflictingAssignmentAfterPendingChange.endDate >= change.startDate &&
                  conflictingAssignmentAfterPendingChange.startDate <= change.endDate
               ) {
                  isStillConflicting = true;
                  break;
               }
            }

            if (!isStillConflicting) {
               if (noLongerConflicting[resourceId]) {
                  noLongerConflicting[resourceId].push(conflict.id);
               } else {
                  noLongerConflicting[resourceId] = [conflict.id];
               }
            }
         }
      }

      if (Object.values(conflictsByResource).filter((a) => a.length).length) {
         showBryntumModal({
            type: BryntumModalType.SchedulingConflict,
            modalId: "gantt-confirmation-modal-container",
            title: "Scheduling conflict",
            body: (
               <div className="schedulingConflictModalBody">
                  <p style={{ marginBottom: "12px" }}>
                     These changes create scheduling conflicts for the following assignments. Would
                     you like to proceed anyway?
                  </p>

                  <div
                     className="unhide-scrollbar"
                     style={{
                        height: "200px",
                        overflow: "scroll",
                        padding: "12px",
                        borderRadius: "4px",
                        border: "solid 1px grey",
                     }}
                  >
                     {Object.entries(conflictsByResource)
                        .filter(([_, conflicts]) => conflicts.length > 0)
                        .map(([resourceId, conflicts], index: number) => (
                           <details
                              style={{
                                 marginBottom: "12px",
                              }}
                              key={index}
                              open
                           >
                              <summary>
                                 {conflicts[0].person_first_name +
                                    " " +
                                    conflicts[0].person_last_name}
                              </summary>
                              <ul
                                 style={{
                                    marginLeft: "32px",
                                    listStyle: "disc",
                                 }}
                              >
                                 {conflicts.map((conflict, index: number) => {
                                    if (noLongerConflicting[resourceId]?.includes(conflict.id))
                                       return;

                                    // For some reason these start/end dates were off by 1 when rendered in the modal
                                    // so we're manually adding 1 day to the dates here before displaying them
                                    const startDate = new Date(conflict.start_day);
                                    startDate.setDate(startDate.getDate() + 1);

                                    const endDate = new Date(conflict.end_day);
                                    endDate.setDate(endDate.getDate() + 1);

                                    return (
                                       <li key={index}>
                                          {conflict.project_name}: {formatDateBryntum(startDate)} -{" "}
                                          {formatDateBryntum(endDate)}
                                       </li>
                                    );
                                 })}
                              </ul>
                           </details>
                        ))}
                  </div>
               </div>
            ),
            onSave: async () => {
               context.finalize(true);
            },
            onCancel: async () => {
               context.finalize(false);
            },
         });
      } else {
         context.finalize(true);
      }
   };

   //#region project model config
   /**
    * Get calendars data and replace timeoff reasons with their translated values
    */
   const calendarsStore = useMemo(
      () =>
         new CalendarManagerStore({
            autoCommit: false,
            syncDataOnLoad: false,
            data: getCalendarsGanttData(ganttData, hasPermissionToViewPeopleTimeoff).map(
               (calendar) => {
                  calendar.intervals = calendar.intervals.map((interval: any) => {
                     if (interval.name) {
                        interval.name = getTimeOffReasonDisplay(interval.name, I18n);
                     }

                     return interval;
                  });
                  return calendar;
               },
            ),
         }),
      [ganttData],
   );
   const tasksData = useMemo(() => {
      return (
         ganttData?.projects.map((project: GanttProject) => {
            const { tasks: projectChildren } = groupTasks(
               project,
               ganttData.people,
               ganttData.jobTitles,
               ganttData.equipment,
               ganttData.equipmentTypes,
               expandedTasks,
               ganttFilter.hideEmptyCategories,
            );

            return {
               ...createProjectTask(project, expandedTasks),
               children: projectChildren,
            };
         }) ?? []
      );
   }, [ganttData, ganttViewMode]);

   //#endregion

   //#region Columns
   // This headerRenderer function expects you to return a string, so we're using the renderToString method to generate
   // static HTML string of our React components. This static HTML does not include any JavaScript or interactivity,
   // such as event handlers like onClick, so we're using a setTimeout to defer the execution of the querySelectors
   // until after the current callstack is clear (ie. until after the header finishes rendering). This async defermnet
   // ensures that the DOM elements are ready and available when we attempt to get them with querySelector.
   const expandCollapseHeaderRenderer: any = ({ headerElement, column }: any) => {
      // These go in the setTimeout to make sure that the children of the headerElement have time to render first.
      setTimeout(() => {
         const expandAllIcon = headerElement.querySelector("#gantt-expand-all-icon") as HTMLElement;
         const collapseAllIcon = headerElement.querySelector(
            "#gantt-collapse-all-icon",
         ) as HTMLElement;

         expandAllIcon?.addEventListener("click", () => {
            expandAllIcon.style.display = "none";
            collapseAllIcon.style.display = "block";
            column.grid.collapseAll();
         });
         collapseAllIcon?.addEventListener("click", () => {
            expandAllIcon.style.display = "block";
            collapseAllIcon.style.display = "none";
            column.grid.expandAll();
         });
      }, 10);

      return `
         ${renderToString(
            <CaretsOutVertical
               id="gantt-expand-all-icon"
               data-testid="gantt-expand-all-icon"
               style={{ display: "block" }}
            />,
         )}
            ${renderToString(
               <CaretsInVertical
                  id="gantt-collapse-all-icon"
                  data-testid="gantt-collapse-all-icon"
                  style={{ display: "none" }}
               />,
            )}
         <span>${column.text}</span>
      `;
   };

   const columns = useMemo<ExtractGanttProp<"columns">>(
      () => [
         {
            alwaysClearCell: false, // v6 defaults this to true, so we're explicitly setting back to false to maintain prior behavior for now
            type: "name",
            text: "Project Name",
            id: "name",
            field: "name",
            width: 250,
            htmlEncode: false,
            hidden: false,
            sortable: true,
            headerRenderer: expandCollapseHeaderRenderer,
            renderer: ({ record, cellElement }: any) =>
               taskRenderer({
                  record,
                  cellElement,
                  ganttViewMode,
                  canViewPeople,
                  canViewProject,
                  personDetailsTearsheetRef,
                  projectDetailsTearsheetRef,
                  ganttRef,
                  expandedTasks,
               }),
         },
         {
            alwaysClearCell: false, // v6 defaults this to true, so we're explicitly setting back to false to maintain prior behavior for now
            type: "name",
            text: "Job Title",
            field: "jobTitleName",
            id: "jobTitleName",
            hidden: false,
            headerRenderer: ({ column }: any) => {
               return `<span>${column.text}</span>`;
            },
         },
         {
            type: "name",
            text: "Type",
            field: "type",
            id: "type",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_project_id",
            field: "projectId",
            id: "projectId",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_project_name",
            field: "projectName",
            id: "projectName",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_category_id",
            field: "parentCategoryId",
            id: "parentCategoryId",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_category_name",
            field: "parentCategoryName",
            id: "parentCategoryName",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_subcategory_id",
            field: "parentSubcategoryId",
            id: "parentSubcategoryId",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "parent_subcategory_name",
            field: "parentSubcategoryName",
            id: "parentSubcategoryName",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            text: "Project Number",
            field: "projectJobNumber",
            id: "projectJobNumber",
            hidden: true,
            exportable: true,
         },
         {
            type: "date",
            field: "startDate",
            id: "startDate",
            text: "Project Start Date",
            sortable: true,
            hidden: false,
            exportable: true,
            flex: 1,
            renderer: ({ record }: any) => {
               const taskType = record.getData("type");
               if (taskType !== TaskType.PROJECT) return null;

               return formatDateBryntum(record.getData("startDate"));
            },
         },
         {
            type: "date",
            field: "startTime",
            id: "startTime",
            text: "Start Time",
            hidden: true,
            exportable: true,
         },
         {
            type: "date",
            field: "endTime",
            id: "endTime",
            text: "End Time",
            hidden: true,
            exportable: true,
         },
         {
            type: "date",
            field: "endDate",
            id: "endDate",
            text: "Project End Date",
            hidden: false,
            sortable: true,
            exportable: true,
            flex: 1,
            renderer: ({ record }: any) => {
               const taskType = record.getData("type");
               if (taskType !== TaskType.PROJECT) return null;

               return formatDateBryntum(record.getData("endDate"));
            },
         },
         {
            type: "calendar",
            field: "workDays",
            id: "workDays",
            text: "Work Days",
            hidden: true,
            exportable: true,
         },
         {
            type: "name",
            field: "resourceId",
            id: "resourceId",
            text: "Resource Name",
            exportable: false,
            flex: 1,
            hidden: true,
            headerRenderer: expandCollapseHeaderRenderer,
            renderer: ({ record, cellElement }: any) => {
               const resourceName = record.firstChild?.getData("resourceName");
               const projectName = record.getData("projectName");

               if (record.getData("type") === undefined) {
                  if (resourceName) {
                     cellElement.classList.add("gantt-grid-resource-text");
                     if (canViewPeople) {
                        return (
                           <Link
                              className="gantt-task-name-link"
                              onClick={() => {
                                 const personId = String(
                                    record.originalData.firstGroupChild.resourceId,
                                 );
                                 personDetailsTearsheetRef.current?.handleOpenTearsheet({
                                    projectId: "",
                                    personId,
                                    callback: () => {},
                                 });
                              }}
                           >
                              {resourceName}
                           </Link>
                        );
                     } else {
                        return <span>{resourceName}</span>;
                     }
                  } else {
                     cellElement.classList.add("gantt-grid-resource-text");
                     return <span>Unassigned</span>;
                  }
               } else {
                  cellElement.classList.add("gantt-grid-project-text");
                  return <span>{projectName}</span>;
               }
            },
            sortable(left: any, right: any) {
               // Will sort only if has children, this is to assure is a grouped item
               if (left.firstChild && right.firstChild) {
                  const name1: string = left.firstChild.getData("resourceName") ?? "";
                  const name2: string = right.firstChild.getData("resourceName") ?? "";
                  return name1.localeCompare(name2);
               }
               return 0;
            },
         },
         {
            type: "date",
            field: "taskStartDate",
            id: "taskStartDate",
            text: "Task Start Date",
            exportable: true,
            flex: 1,
            hidden: false,
            renderer: ({ record }: any) => {
               const taskType = record.getData("type");
               if (REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(taskType)) {
                  const startDate = record.getData("startDate");
                  return startDate ? formatDateBryntum(startDate) : "";
               } else return "";
            },
            sortable: (left: any, right: any) => {
               if (!left || !right) return 0;

               const leftTaskType = left.getData("type");
               const rightTaskType = right.getData("type");

               if (
                  REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(leftTaskType) &&
                  REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(rightTaskType)
               ) {
                  const leftParentId =
                     left.getData("parentCategoryId") || left.getData("parentProjectId");
                  const rightParentId =
                     right.getData("parentCategoryId") || right.getData("parentProjectId");

                  // Only sort if both records have the same parent to keep the categories and projects in order
                  if (leftParentId === rightParentId) {
                     const startDate1 = new Date(left.getData("startDate") || 0);
                     const startDate2 = new Date(right.getData("startDate") || 0);
                     return startDate1.getTime() - startDate2.getTime();
                  }
               }
               return 0;
            },
         },
         {
            type: "date",
            field: "taskEndDate",
            id: "taskEndDate",
            text: "Task End Date",
            exportable: true,
            flex: 1,
            hidden: false,
            renderer: ({ record }: any) => {
               const taskType = record.getData("type");
               if (REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(taskType)) {
                  const endDate = record.getData("endDate");
                  return endDate ? formatDateBryntum(endDate) : "";
               } else return "";
            },
            sortable: (left: any, right: any) => {
               if (!left || !right) return 0;

               const leftTaskType = left.getData("type");
               const rightTaskType = right.getData("type");

               if (
                  REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(leftTaskType) &&
                  REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(rightTaskType)
               ) {
                  const leftParentId =
                     left.getData("parentCategoryId") || left.getData("parentProjectId");
                  const rightParentId =
                     right.getData("parentCategoryId") || right.getData("parentProjectId");

                  // Only sort if both records have the same parent to keep the categories and projects in order
                  if (leftParentId === rightParentId) {
                     const endDate1 = new Date(left.getData("endDate") || 0);
                     const endDate2 = new Date(right.getData("endDate") || 0);
                     return endDate1.getTime() - endDate2.getTime();
                  }
               }
               return 0;
            },
         },
      ],
      [canViewProject],
   );

   const strips = useMemo<ExtractGanttProp<"strips">>(() => {
      const values = getGanttConfigurePanelValues();
      const items = buildCommonConfigurePanelItems({
         t: (key) => I18n.t(key),
         values,
         includeTimeOffToggle: hasPermissionToViewPeopleTimeoff,
         onProjectInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showProjectInformation", value);
            ganttRef.current?.instance?.element?.classList.toggle(
               "hide-project-information",
               !value,
            );
         },
         onAllocationInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showAllocationInformation", value);
            ganttRef.current?.instance?.element?.classList.toggle(
               "hide-allocation-information",
               !value,
            );
         },
         onTimeOffInformationChange: (value) => {
            updateGanttConfigurePanelLocalStorage("showTimeoff", value);
            ganttRef.current?.instance?.element?.classList.toggle("hide-time-off-reasons", !value);
         },
         onHideWeekendsChange: (value) => {
            updateGanttConfigurePanelLocalStorage("hideWeekends", value);
            if (ganttRef.current?.instance) {
               hideWeekends(ganttRef.current.instance, value);
            }
         },
         onRowHeightInput: (value) => {
            if (!ganttRef.current?.instance) return;
            ganttRef.current.instance.rowHeight = value;
            ganttRef.current.instance.element.style.setProperty(
               "--non-working-time-padding-top",
               `${(value - 15) / 2}px`,
            );
            ganttRef.current.instance.element.style.setProperty(
               "--non-working-time-height",
               `${value - 1}px`,
            );
         },
         onRowHeightChange: (value) => {
            updateGanttConfigurePanelLocalStorage("rowHeight", value);
            // TODO: Whenever this bug-fix gets released by Bryntum, we should be able to remove the below here
            // Bug ticket: https://github.com/bryntum/support/issues/9698
            if (ganttRef.current?.instance?.features?.taskNonWorkingTime) {
               ganttRef.current.instance.features.taskNonWorkingTime.disabled = true;
               ganttRef.current.instance.features.taskNonWorkingTime.disabled = false;
            }
         },
         onTaskBorderRadiusInput: (value) => {
            ganttRef.current?.instance?.element?.style.setProperty(
               "--task-border-radius",
               `${value}px`,
            );
         },
         onTaskBorderRadiusChange: (value) => {
            updateGanttConfigurePanelLocalStorage("taskBorderRadius", value);
         },
         onAssignmentBarColorsChange: (value) => {
            updateGanttConfigurePanelLocalStorage("assignmentBarColors", value);
            ganttRef.current?.instance?.refreshRows();
         },
         extraItems: [
            {
               type: "slidetoggle",
               label: I18n.t("views.company.workforce_planning.gantt.configure_panel.task_labels"),
               labelPosition: "after",
               value: values.taskLabels,
               onChange: ({ value }: any) => {
                  updateGanttConfigurePanelLocalStorage("taskLabels", value);
                  ganttRef.current?.instance?.element?.classList.toggle("hide-task-labels", !value);
               },
               id: "task-labels",
            },
            showGanttTotalsConfig && {
               tag: "span",
               html: I18n.t("views.company.workforce_planning.gantt.configure_panel.totals_title"),
               style: "margin-top: 2em",
            },
            showGanttTotalsConfig && {
               type: "slidetoggle",
               label: I18n.t(
                  "views.company.workforce_planning.gantt.configure_panel.assignment_totals",
               ),
               labelPosition: "after",
               value: values.showTotals,
               onChange: ({ value }: any) => {
                  updateGanttConfigurePanelLocalStorage("showTotals", value);
                  ganttRef.current?.instance?.refreshRows();
               },
               id: "show-totals",
            },
            showGanttTotalsConfig && {
               type: "combo",
               label: I18n.t(
                  "views.company.workforce_planning.gantt.configure_panel.totals_cell_units",
               ),
               value: values.totalsCellUnits || "People",
               items: [
                  {
                     value: TotalUnitType.PEOPLE,
                     text: I18n.t(
                        "views.company.workforce_planning.gantt.configure_panel.totals_units_people",
                     ),
                  },
                  {
                     value: TotalUnitType.HOURS,
                     text: I18n.t(
                        "views.company.workforce_planning.gantt.configure_panel.totals_units_hours",
                     ),
                  },
                  {
                     value: TotalUnitType.COST,
                     text: I18n.t(
                        "views.company.workforce_planning.gantt.configure_panel.totals_units_cost",
                     ),
                  },
                  {
                     value: TotalUnitType.MAN_DAYS,
                     text: I18n.t(
                        "views.company.workforce_planning.gantt.configure_panel.totals_units_man_days",
                     ),
                  },
               ],
               onChange: ({ value }: any) => {
                  updateGanttConfigurePanelLocalStorage("totalsCellUnits", value);
                  ganttRef.current?.instance?.refreshRows();
               },
               id: "totals-cell-units",
            },
         ],
      });

      return buildConfigurePanelStrip(items) as ExtractGanttProp<"strips">;
   }, [I18n, hasPermissionToViewPeopleTimeoff, showGanttTotalsConfig]);
   //#endregion

   //#region Features
   const taskNonWorkingTimeFeature = useMemo<ExtractGanttProp<"taskNonWorkingTimeFeature">>(
      () => ({
         mode: "both",
         tooltipTemplate({ name: timeOffReason, startDate, endDate, taskRecord }) {
            // don't show tooltip for users without permission to view time off
            if (!hasPermissionToViewPeopleTimeoff) return;
            // don't show tooltip for weekends
            if (!timeOffReason) return;

            const dateFormat = timeFormat("%Y-%m-%d");
            const intervalId = `${dateFormat(startDate)}_${dateFormat(endDate)}`;
            const name = taskRecord.getData("name");
            const jobTitleName = taskRecord.getData("jobTitleName");
            const jobTitleColor = taskRecord.getData("jobTitleColor");
            const calendarId = taskRecord.getData("calendar");
            const currentCalendar = calendarsStore.data.find(
               (calendar: any) => calendar.id === calendarId,
            );
            // time-off interval ID includes a date part and a unique id, we need to separate and verify the date portion
            const currentTimeOffInterval = (currentCalendar as any)?.intervals.find(
               (interval: any) => interval.id?.split("__")[0] === intervalId,
            );

            if (!currentTimeOffInterval) return;

            endDate.setDate(endDate.getDate() - 1); // we want to show the last day of the time off at the tooltip
            const { startTime, endTime, repeat, isPaid, applyToSaturday, applyToSunday } =
               currentTimeOffInterval;

            return renderTimeOffTooltip({
               name,
               jobTitleName,
               jobTitleColor,
               timeOffReason,
               startDate,
               endDate,
               startTime,
               endTime,
               isPaid,
               applyToSaturday,
               applyToSunday,
               repeat,
               I18n,
            });
         },
      }),
      [ganttData],
   );

   const taskResizeFeature = useMemo<ExtractGanttProp<"taskResizeFeature">>(
      () => ({
         tooltipTemplate({ startDate, endDate, record }) {
            // For some reason the taskDragFeature respects the fact that our endDate is set to 11:59 PM of it's proper day, but this taskResizeFeature
            // is rounding up to midnight of the next day! To safely correct this, we're simply subtracting one minute from whatever the endDate
            // received here is. Now if Bryntum happens to fix this bug it will just decrement to 11:58 PM of the proper day and that's no big deal at all.
            // As long as it's at the end of the proper day, we're good!
            endDate.setMinutes(endDate.getMinutes() - 1);
            return tooltipTemplateCallback(record, startDate, endDate);
         },
         tip: {
            htmlCls: "taskDragTooltipWrapper resize",
            bodyCls: "taskDragTooltip",
         },
      }),
      [],
   );

   const taskDragFeature = useMemo<ExtractGanttProp<"taskDragFeature">>(
      () => ({
         dragAllSelectedTasks: true,
         tooltipTemplate({ startDate, endDate, taskRecord }) {
            // Be sure that endDate is not rounding up to next day by simply subtracting one minute from whatever the endDate received here is.
            endDate.setMinutes(endDate.getMinutes() - 1);

            return tooltipTemplateCallback(taskRecord, startDate, endDate);
         },
         tip: {
            htmlCls: "taskDragTooltipWrapper",
            bodyCls: "taskDragTooltip",
         },
      }),
      [],
   );

   const excelExporterFeature = useMemo<ExtractGanttProp<"excelExporterFeature">>(
      () => ({
         xlsProvider: MyXlsProvider,
         exporterConfig: {
            columns: [
               {
                  type: "name",
                  text: "type",
                  field: "type",
               },
               {
                  type: "name",
                  text: "id",
                  field: "id",
               },
               {
                  type: "name",
                  text: "name",
                  field: "name",
               },
               {
                  type: "name",
                  text: "job_title",
                  field: "jobTitleName",
               },
               {
                  type: "name",
                  text: "parent_project_id",
                  field: "projectId",
               },
               {
                  type: "name",
                  text: "parent_project_name",
                  field: "projectName",
               },
               {
                  type: "name",
                  text: "parent_category_id",
                  field: "parentCategoryId",
               },
               {
                  type: "name",
                  text: "parent_category_name",
                  field: "parentCategoryName",
               },
               {
                  type: "name",
                  text: "parent_subcategory_id",
                  field: "parentSubcategoryId",
               },
               {
                  type: "name",
                  text: "parent_subcategory_name",
                  field: "parentSubcategoryName",
               },
               { type: "name", text: "project_number", field: "projectJobNumber" },
               {
                  type: "startdate",
                  field: "startDate",
                  text: "start_date",
                  renderer: ({ value }: { value?: Date }) => {
                     if (!value) return;

                     return formatDateBryntum(value);
                  },
               },
               {
                  type: "enddate",
                  field: "endDate",
                  text: "end_date",
                  renderer: ({ value }: { value?: Date }) => {
                     if (!value) return;

                     return formatDateBryntum(value);
                  },
               },
               {
                  type: "date",
                  field: "startTime",
                  text: "start_time",
                  renderer: ({ value }: { value?: string }) => {
                     if (!value) return;

                     return formatTimeBryntum(value);
                  },
               },
               {
                  type: "date",
                  field: "endTime",
                  text: "end_time",
                  renderer: ({ value }: { value?: string }) => {
                     if (!value) return;

                     return formatTimeBryntum(value);
                  },
               },
               {
                  type: "calendar",
                  field: "workDays",
                  text: "work_days",
                  renderer: ({ value }: { value?: Record<number, boolean> }) => {
                     if (!value) return;

                     return DAYS.filter((_, index) => value[index]).join(" ");
                  },
               },
            ],
         },
      }),
      [],
   );

   const labelsFeature = useMemo<ExtractGanttProp<"labelsFeature">>(
      () => ({
         bottom: {
            renderer: ({ taskRecord }: any) => {
               if (taskRecord.getData("type") !== TaskType.PROJECT) return null;

               // Need to be retrieved from instance, state is not updated yet at this point,
               // if retrieving from state it will be the previous value
               const viewPreset = (ganttRef.current?.instance.viewPreset as ViewPreset)?.base;
               if (!viewPreset) return null;
               if (!getGanttConfigurePanelValues().showTotals || !showGanttTotalsConfig)
                  return null;

               const {
                  projectAssignments: assignments,
                  wageOverrides = [],
                  startDate,
                  endDate,
                  dailyEndTime,
                  dailyStartTime,
               } = taskRecord;

               const projectId = taskRecord.get("id");
               const totalsUnit =
                  (getGanttConfigurePanelValues().totalsCellUnits as TotalUnitType) ??
                  TotalUnitType.PEOPLE;
               const hideWeekendsValue = getGanttConfigurePanelValues().hideWeekends;

               let interval: moment.unitOfTime.StartOf;
               switch (viewPreset) {
                  case "weekAndMonth":
                  case "weekDateAndMonth":
                     interval = "week";
                     break;
                  case "monthAndYear":
                     interval = "month";
                     break;
                  case "year-200by100":
                  case "year":
                  case "year-50by100":
                     interval = "quarter";
                     break;
                  case "weekAndDayLetter":
                  default:
                     interval = "day";
               }

               ganttTotalService.addProject(
                  {
                     id: projectId,
                     assignments,
                     start_date: startDate,
                     est_end_date: endDate,
                     daily_start_time: dailyStartTime,
                     daily_end_time: dailyEndTime,
                  } as GanttProject,
                  ganttData?.people ?? [],
                  paidShiftHours,
                  wageOverrides,
               );

               let workedByGranularity = ganttTotalService.getTotalsByUnit(
                  projectId,
                  interval,
                  totalsUnit,
               );

               if (interval === "day") {
                  workedByGranularity = workedByGranularity.filter((day: TotalByDay) => {
                     return hideWeekendsValue && [0, 6].includes(moment(day.date).day())
                        ? null
                        : day;
                  });
               }

               const tickDate = new Date(startDate);
               const tickSize = ganttRef.current?.instance.tickSize ?? 0;
               const currentTick =
                  ganttRef.current?.instance.timeAxis.getTickFromDate(tickDate) ?? -1;
               const tickStart = currentTick % 1;

               return {
                  tag: "div",
                  "data-testid": "totals-units-row",
                  style: {
                     display: "flex",
                     position: "relative",
                     left: tickSize * tickStart * -1,
                     justifySelf: "flex-start",
                  },
                  children: workedByGranularity.map((worked) => {
                     const value = Number(worked[totalsUnit]);
                     let displayText: string;
                     // Apply appropriate formatting based on totals unit type
                     if (totalsUnit === TotalUnitType.COST) {
                        displayText = currencyFormatter.formatCurrency(value);
                     } else {
                        displayText = numberFormatter.formatNumber(value);
                     }

                     return {
                        style: {
                           width: tickSize,
                        },
                        text: displayText,
                     };
                  }),
               };
            },
         },
      }),
      [
         paidShiftHours,
         ganttViewMode,
         ganttTotalService,
         ganttRef,
         numberFormatter,
         currencyFormatter,
      ],
   );

   const isExpanded = localStorage.getItem("gantt-expanded") === "true";
   const treeGroupFeature = useMemo<ExtractGanttProp<"treeGroupFeature">>(() => {
      return {
         levels: ganttViewMode === GanttViewMode.PROJECTS ? null : ["name"],
         expandParents: isExpanded,
      };
   }, [ganttViewMode, isExpanded]);
   //#endregion

   //#region Gantt Events
   // Create the store once with minimal configuration
   const taskStore = useMemo<TaskStore>(() => {
      return new TaskStore({
         modelClass: ProcoreTaskModel,
         fields: ["name", "resourceId"],
         syncDataOnLoad: false,
      });
   }, []);

   // Update the store's event handlers whenever relevant dependencies change
   useEffect(() => {
      // Create a closure that captures the current fetchData
      taskStore.onBeforeSort = ({ sorters }) => {
         // This function is recreated every time the effect runs,
         // so it always has the latest fetchData
         return handleBeforeSort(sorters, sortDirection, projectSort, skip, fetchData);
      };
   }, [taskStore, ganttFilter, search]);

   //#endregion

   const handleLoadMore = useCallback(async () => {
      if (isLoadingMoreRef.current) {
         return;
      }
      if (hasAllRecords.current) {
         return;
      }
      isLoadingMoreRef.current = true;
      skip.current += GANTT_PAGE_SIZE;
      await fetchData();
      isLoadingMoreRef.current = false;
   }, [ganttFilter, search, ganttViewMode]);

   // Use scroll-based infinite loading for Bryntum v7
   useGanttInfiniteScroll({
      ganttInstance: ganttRef.current?.instance,
      isInitialLoadCompleteRef: isInitialLoadComplete,
      hasAllRecordsRef: hasAllRecords,
      isLoadingRef: isLoadingMoreRef,
      onLoadMore: handleLoadMore,
      threshold: 200,
   });

   const ganttConfig = useMemo(() => {
      const config = initGanttConfig(ganttRef, I18n);

      if (!checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS)) {
         config.rowCopyPasteFeature = false;
         config.taskCopyPasteFeature = false;
      }

      return config;
   }, [ganttRef]);

   const schedulerProLoadParams = useMemo(
      () => ({
         group_id: groupId,
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         sortDirection: sortDirection.current,
         sort: "name",
         filters: getFilterFormatted(ganttFilter, ganttViewMode),
         search,
      }),
      [groupId, ganttFilter, ganttViewMode, search],
   );

   const activeRef = isSchedulerProView ? schedulerRef : ganttRef;

   const initialSchedulerDateRange =
      isSchedulerProView && selectedDateRange != null
         ? getSchedulerInitialDateRange(selectedDateRange)
         : undefined;

   return (
      <ToolLandingPage className="all-descendants-border-box">
         <ToolLandingPage.Main id="gantt-tool-landing-page-main">
            <ToolLandingPage.Body>
               <SplitViewCard>
                  <SplitViewCard.Main style={{ overflow: "auto" }}>
                     <SplitViewCard.Section>
                        <Box style={{ display: "flex", overflow: "hidden" }}>
                           <GanttFilterPanel
                              ganttFilter={ganttFilter}
                              setGanttFilter={setGanttFilter}
                              disabled={isMaskOn}
                              onClose={() => setFilterPanelOpen(false)}
                              customFilterFields={customFilterFields}
                              isOpen={filterPanelOpen}
                              ganttViewMode={ganttViewMode}
                           />
                           <Box className="detailPageSection">
                              <Spinner
                                 loading={!isInitialLoadComplete.current}
                                 data-testid="loading-spinner"
                                 label={I18n.t("views.company.workforce_planning.loading")}
                              >
                                 <GanttControlPanel
                                    search={search}
                                    setSearch={setSearch}
                                    ganttViewMode={ganttViewMode}
                                    setGanttViewMode={(value) => setGanttViewMode(value)}
                                    toggleFilterPanel={() =>
                                       setFilterPanelOpen((prevState) => !prevState)
                                    }
                                    toggleConfigPanel={() => {
                                       const rightPanel = activeRef.current?.instance.widgetMap
                                          ?.right as Panel;
                                       if (rightPanel) {
                                          rightPanel.toggleCollapsed();
                                       }
                                    }}
                                    ganttFilter={ganttFilter}
                                    setGanttFilter={setGanttFilter}
                                    onViewDateChange={(date) => {
                                       const targetDate = date ?? new Date();
                                       if (selectedDateRange !== null) {
                                          applyDateRange(selectedDateRange, targetDate);
                                       } else {
                                          activeRef.current?.instance.scrollToDate(targetDate, {
                                             block: "start",
                                          });
                                       }
                                    }}
                                    onZoomOut={() => {
                                       activeRef.current?.instance.zoomOut();
                                       if (selectedDateRange !== null) resetDateRange();
                                    }}
                                    onZoomIn={() => {
                                       activeRef.current?.instance.zoomIn();
                                       if (selectedDateRange !== null) resetDateRange();
                                    }}
                                    onZoomToFit={() => {
                                       activeRef.current?.instance.zoomToFit();
                                       if (selectedDateRange !== null) resetDateRange();
                                    }}
                                    selectedDateRange={selectedDateRange}
                                    onDateRangeChange={(days, anchor) =>
                                       applyDateRange(days, anchor)
                                    }
                                    fileExportName={fileExportName}
                                    ganttRef={ganttRef}
                                    customFilterFields={customFilterFields}
                                 />
                                 {isInitialLoadComplete.current && isSchedulerProView ? (
                                    <ResourceSchedule
                                       paidShiftHours={paidShiftHours}
                                       personDetailsTearsheetRef={personDetailsTearsheetRef}
                                       editAssignmentTearsheetRef={editAssignmentTearsheetRef}
                                       createAssignmentTearsheetRef={createAssignmentTearsheetRef}
                                       canViewPeople={canViewPeople}
                                       loadParams={schedulerProLoadParams}
                                       availablePresets={availablePresets}
                                       viewPreset={viewPreset}
                                       schedulerRef={schedulerRef}
                                       hasPermissionToViewPeopleTimeoff={
                                          hasPermissionToViewPeopleTimeoff
                                       }
                                       initialDateRange={initialSchedulerDateRange}
                                    />
                                 ) : (
                                    ganttData && (
                                       <>
                                          <BryntumGanttProjectModel
                                             ref={projectRef}
                                             {...projectConfig}
                                             onBeforeSend={(event) => {
                                                const { requestConfig }: { requestConfig: any } =
                                                   event;
                                                const taskStoreChanges =
                                                   ganttRef.current?.instance.taskStore.changes;
                                                const parsedBody = JSON.parse(requestConfig.body);

                                                if (!parsedBody.tasks) return Promise.resolve();

                                                if (parsedBody.tasks.removed) {
                                                   parsedBody.tasks.removed =
                                                      parsedBody.tasks.removed.map((rem1: any) => {
                                                         const taskRecord =
                                                            taskStoreChanges?.removed?.find(
                                                               (rem2) =>
                                                                  rem2.getData("id") === rem1.id,
                                                            );

                                                         return {
                                                            id: taskRecord?.getData("id"),
                                                            projectId:
                                                               taskRecord?.getData("projectId"),
                                                            type: taskRecord?.getData("type"),
                                                            categoryId:
                                                               taskRecord?.getData("categoryId"),
                                                            subcategoryId:
                                                               taskRecord?.getData("subcategoryId"),
                                                         };
                                                      });
                                                } else if (parsedBody.tasks.updated) {
                                                   parsedBody.tasks.updated =
                                                      parsedBody.tasks.updated.map(
                                                         (updatedTask: any) => {
                                                            if (updatedTask.parentId) {
                                                               const newParent =
                                                                  ganttRef.current!.instance.taskStore.getById(
                                                                     updatedTask.parentId,
                                                                  );
                                                               if (newParent) {
                                                                  const parentType =
                                                                     newParent.getData("type");
                                                                  updatedTask.parentType =
                                                                     parentType;
                                                                  updatedTask.parentProjectId =
                                                                     parentType === TaskType.PROJECT
                                                                        ? newParent.getData("id")
                                                                        : newParent.getData(
                                                                             "projectId",
                                                                          );
                                                                  updatedTask.parentCategoryId =
                                                                     parentType ===
                                                                     TaskType.CATEGORY
                                                                        ? newParent.getData("id")
                                                                        : newParent.getData(
                                                                             "categoryId",
                                                                          );
                                                                  updatedTask.parentSubcategoryId =
                                                                     parentType ===
                                                                     TaskType.SUBCATEGORY
                                                                        ? newParent.getData("id")
                                                                        : newParent.getData(
                                                                             "subcategoryId",
                                                                          );
                                                               }
                                                            }

                                                            if (
                                                               updatedTask.type === "category" &&
                                                               !updatedTask.categoryId
                                                            ) {
                                                               updatedTask.categoryId =
                                                                  updatedTask.id;
                                                            } else if (
                                                               updatedTask.type === "subcategory" &&
                                                               !updatedTask.subcategoryId
                                                            ) {
                                                               updatedTask.subcategoryId =
                                                                  updatedTask.id;
                                                            }
                                                            return updatedTask;
                                                         },
                                                      );
                                                }

                                                requestConfig.body = JSON.stringify(parsedBody);
                                                return Promise.resolve();
                                             }}
                                             tasks={tasksData}
                                             taskStore={taskStore}
                                             calendarManagerStore={calendarsStore}
                                             onDataReady={() => {
                                                console.log(
                                                   "TASKS: ",
                                                   ganttRef.current?.instance.tasks.length,
                                                );
                                             }}
                                             onLoad={() => {
                                                ganttRef.current?.instance.unmask();
                                                setIsMaskOn(false);
                                             }}
                                          />
                                          <BryntumGantt
                                             ref={ganttRef}
                                             {...ganttConfig}
                                             project={projectRef}
                                             labelsFeature={labelsFeature}
                                             onBeforePresetChange={(preset: any) => {
                                                if (preset.from === undefined) return;
                                                localStorage.setItem(
                                                   "gantt-view-preset",
                                                   String(preset.to.id),
                                                );
                                             }}
                                             onDateRangeChange={(e) => {
                                                setCurrentGanttRange({
                                                   startDay: getDetachedDay(e.new.startDate),
                                                   endDay: getDetachedDay(e.new.endDate),
                                                });
                                             }}
                                             onExpandNode={({ record }) => {
                                                const id =
                                                   ganttViewMode === GanttViewMode.PROJECTS
                                                      ? record.get("id")
                                                      : "generated_" +
                                                        record.firstChild.getData("resourceId") +
                                                        "_";

                                                expandTask(id);
                                             }}
                                             onCollapseNode={({ record }) => {
                                                const id =
                                                   ganttViewMode === GanttViewMode.PROJECTS
                                                      ? record.get("id")
                                                      : "generated_" +
                                                        record.firstChild.getData("resourceId") +
                                                        "_";

                                                collapseTask(id);
                                             }}
                                             treeGroupFeature={treeGroupFeature}
                                             startDate={startDate.current}
                                             endDate={endDate.current}
                                             presets={availablePresets}
                                             viewPreset={viewPreset}
                                             rowHeight={
                                                getGanttConfigurePanelValues().rowHeight || 24
                                             }
                                             columns={columns}
                                             strips={strips}
                                             taskNonWorkingTimeFeature={taskNonWorkingTimeFeature}
                                             taskTooltipFeature={{
                                                maxWidth: "unset",
                                                minWidth: "400px",
                                                width: "max-content",
                                                style: "border-radius: 6px;",
                                                template: ({ taskRecord }: any) => {
                                                   /*
                                             Getting the constructed data here and passing in as props so we can
                                             run tests without running into Bryntum's 'getData' function.
                                          */
                                                   const isSegment =
                                                      taskRecord instanceof EventSegmentModel;
                                                   const parentTask = isSegment
                                                      ? taskRecord.task
                                                      : taskRecord;
                                                   const id = parentTask.getData("id");
                                                   const type = taskRecord.getData("type");
                                                   const startDate =
                                                      taskRecord.startDate ?? parentTask.startDate;
                                                   const endDate =
                                                      taskRecord.endDate ?? parentTask.endDate;

                                                   const globalProps = {
                                                      type,
                                                      dateRangeString: getDatesRangeString(
                                                         startDate,
                                                         endDate,
                                                      ),
                                                      name:
                                                         taskRecord.getData("name") ??
                                                         parentTask.getData("name"),
                                                      projectId:
                                                         taskRecord.getData("projectId") ??
                                                         parentTask.getData("projectId"),
                                                      projectName:
                                                         taskRecord.getData("projectName") ??
                                                         parentTask.getData("projectName"),
                                                      projectColor:
                                                         taskRecord.getData("projectColor") ??
                                                         parentTask.getData("projectColor"),
                                                      projectJobNumber:
                                                         taskRecord.getData("projectJobNumber") ??
                                                         parentTask.getData("projectJobNumber"),
                                                      categoryName:
                                                         taskRecord.getData("categoryName") ??
                                                         parentTask.getData("categoryName"),
                                                      jobTitleColor:
                                                         taskRecord.getData("jobTitleColor") ??
                                                         parentTask.getData("jobTitleColor"),
                                                      jobTitleName:
                                                         taskRecord.getData("jobTitleName") ??
                                                         parentTask.getData("jobTitleName"),
                                                      workDays:
                                                         taskRecord.getData("workDays") ??
                                                         parentTask.getData("workDays"),
                                                      paidShiftHours,
                                                   };

                                                   if (type === TaskType.PROJECT) {
                                                      const dailyStartTime =
                                                         taskRecord.getData("dailyStartTime");
                                                      const dailyEndTime =
                                                         taskRecord.getData("dailyEndTime");
                                                      const startTime =
                                                         formatTimeBryntum(dailyStartTime);
                                                      const endTime =
                                                         formatTimeBryntum(dailyEndTime);
                                                      const props = {
                                                         ...globalProps,
                                                         startTime,
                                                         endTime,
                                                      };
                                                      return <GanttTooltipComponent {...props} />;
                                                   } else if (
                                                      REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(type) ||
                                                      isSegment
                                                   ) {
                                                      const parentTask = isSegment
                                                         ? taskRecord.task
                                                         : taskRecord;
                                                      const dailyStartTime =
                                                         parentTask.getData("startTime");
                                                      const dailyEndTime =
                                                         parentTask.getData("endTime");
                                                      const startTime = dailyStartTime
                                                         ? formatTimeBryntum(dailyStartTime)
                                                         : null;
                                                      const endTime = dailyEndTime
                                                         ? formatTimeBryntum(dailyEndTime)
                                                         : null;

                                                      const taskTimeAllocation =
                                                         getTaskTimeAllocation(
                                                            startTime,
                                                            endTime,
                                                            globalProps.projectId,
                                                            id,
                                                         );
                                                      const isTaskWithTime =
                                                         Boolean(startTime) && Boolean(endTime);
                                                      const instructionText =
                                                         parentTask.getData("instructionText");
                                                      const workScopeText =
                                                         parentTask.getData("workScopeText");
                                                      const workingDayClass = (day: number) =>
                                                         globalProps.workDays?.[day]
                                                            ? "bold"
                                                            : "thin";

                                                      const props = {
                                                         ...globalProps,
                                                         type: isSegment
                                                            ? TaskType.ASSIGNMENT
                                                            : type,
                                                         startTime,
                                                         endTime,
                                                         isTaskWithTime,
                                                         isRequest: REQUEST_TASK_TYPES.has(type),
                                                         isEquipment:
                                                            EQUIPMENT_TASK_TYPES.has(type),
                                                         taskName:
                                                            type === TaskType.REQUEST
                                                               ? "Request"
                                                               : type === TaskType.EQUIPMENT_REQUEST
                                                               ? "Equipment Request"
                                                               : globalProps.name,
                                                         taskTimeAllocation,
                                                         instructionText,
                                                         workScopeText,
                                                         workingDayClass,
                                                      };

                                                      return <GanttTooltipComponent {...props} />;
                                                   } else if (
                                                      type === TaskType.CATEGORY ||
                                                      type === TaskType.SUBCATEGORY
                                                   ) {
                                                      return (
                                                         <GanttTooltipComponent {...globalProps} />
                                                      );
                                                   } else {
                                                      return "";
                                                   }
                                                },
                                             }}
                                             taskResizeFeature={taskResizeFeature}
                                             excelExporterFeature={excelExporterFeature}
                                             printFeature
                                             onRenderRows={(event) => {
                                                const { source } = event;

                                                if (skip.current > 0 && ganttRef.current) {
                                                   ganttRef.current.instance.restoreScroll();
                                                }
                                                source.element.style.setProperty(
                                                   "--task-border-radius",
                                                   `${
                                                      getGanttConfigurePanelValues()
                                                         .taskBorderRadius
                                                   }px`,
                                                );
                                                source.element.style.setProperty(
                                                   "--row-height",
                                                   `${getGanttConfigurePanelValues().rowHeight}px`,
                                                );
                                                source.element.style.setProperty(
                                                   "--non-working-time-padding-top",
                                                   `${
                                                      (getGanttConfigurePanelValues().rowHeight -
                                                         15) /
                                                      2
                                                   }px`,
                                                );
                                                source.element.style.setProperty(
                                                   "--non-working-time-height",
                                                   `${
                                                      getGanttConfigurePanelValues().rowHeight - 1
                                                   }px`,
                                                );
                                                hideWeekends(
                                                   source as Gantt,
                                                   getGanttConfigurePanelValues().hideWeekends,
                                                );
                                             }}
                                             onRenderRow={({ record, row }) => {
                                                // jobTitleColor is available as a custom property because we explicitly added it when we were building the assignments and requests data
                                                // for the tasksData object in our ProjectModel
                                                const jobTitleColor =
                                                   record.getData("jobTitleColor");

                                                const isEquipment = EQUIPMENT_TASK_TYPES.has(
                                                   record.getData("type"),
                                                );

                                                const leafIcon = row.elements.locked.querySelector(
                                                   `[data-column='name'] i.b-icon.b-tree-icon.b-icon-tree-leaf`,
                                                );
                                                const excavatorIcon =
                                                   row.elements.locked.querySelector(
                                                      ".excavator-icon",
                                                   );

                                                if (leafIcon) {
                                                   if (isEquipment && !excavatorIcon) {
                                                      // For equipment assignments we need to replace leaf color icon with excavator icon
                                                      leafIcon.setAttribute("hidden", "true");
                                                      const excavatorIcon = renderToString(
                                                         <div className="excavator-icon">
                                                            <Excavator
                                                               size="sm"
                                                               style={{ margin: "0 4px" }}
                                                            />
                                                         </div>,
                                                      );
                                                      leafIcon.parentElement?.insertAdjacentHTML(
                                                         "afterbegin",
                                                         excavatorIcon,
                                                      );
                                                   } else if (jobTitleColor) {
                                                      leafIcon.removeAttribute("hidden");
                                                      excavatorIcon?.remove();
                                                      leafIcon.setAttribute(
                                                         "style",
                                                         `color: ${jobTitleColor}`,
                                                      );
                                                   }
                                                }

                                                // Add horizontal rule below the projectName row cells. The way that Bryntum rows are rendered are a bit weird, so in order to work properly,
                                                // javascript needs to be paired with the CSS rules for "hr.display = 'none'" that are found in gantt.styl
                                                if (
                                                   record.getData("type") === TaskType.PROJECT &&
                                                   row.element.querySelector("hr") == null
                                                ) {
                                                   const hr = document.createElement("hr");
                                                   hr.setAttribute(
                                                      "style",
                                                      "width: 100%; opacity: 0.5; margin: 0; position: absolute; bottom: -1px;",
                                                   );
                                                   row.element.appendChild(hr);
                                                }
                                             }}
                                             taskRenderer={({ taskRecord, renderData }) => {
                                                const isSegment =
                                                   taskRecord instanceof EventSegmentModel;
                                                const parentTask = isSegment
                                                   ? taskRecord.task
                                                   : taskRecord;
                                                const id = parentTask.getData("id");
                                                const projectId = parentTask.getData("projectId");
                                                const projectJobNumber =
                                                   parentTask.getData("projectJobNumber");
                                                const entityType = taskRecord.getData("type");
                                                const projectColor =
                                                   taskRecord.getData("projectColor");
                                                const requestsNumber =
                                                   taskRecord.getData("requestsNumber");
                                                const isProjectEntity =
                                                   entityType === TaskType.PROJECT;
                                                const isCategoryEntity =
                                                   entityType === TaskType.CATEGORY ||
                                                   entityType === TaskType.SUBCATEGORY;
                                                const isTaskEntity =
                                                   REQUEST_AND_ASSIGNMENT_TASK_TYPES.has(
                                                      entityType,
                                                   );

                                                const [baseColor, lightColor, lightestColor] =
                                                   getColorShades(projectColor);

                                                const hasSegments =
                                                   taskRecord?.segments?.length > 0;

                                                const selectedBarColor =
                                                   getSelectedBarColorHexValues(
                                                      taskRecord,
                                                      paidShiftHours,
                                                   );

                                                // Adjusts task bar colors based on type of task:
                                                //   - projects will be base
                                                //   - categories/subcategories will be lighter
                                                //   - assignments will be chosen by they color configured in the gantt config panel
                                                //   - requests will be white with the chosen color as a dashed border
                                                if (!isSegment && !hasSegments) {
                                                   if (isProjectEntity) {
                                                      renderData.style += `
                                                      background-color: ${baseColor};
                                                      color: ${getTextColorForBackground(
                                                         baseColor,
                                                      )};
                                                   `;
                                                   } else if (isCategoryEntity) {
                                                      renderData.style += `background-color: ${lightColor};`;
                                                   } else if (isTaskEntity) {
                                                      renderData.style += `text-align: right;`;

                                                      if (ASSIGNMENT_TASK_TYPES.has(entityType)) {
                                                         let backgroundColor = selectedBarColor;
                                                         const assignmentBarColor =
                                                            getGanttConfigurePanelValues()
                                                               .assignmentBarColors;
                                                         if (
                                                            assignmentBarColor ===
                                                               AssignmentBarColors.PROJECT ||
                                                            assignmentBarColor === ""
                                                         ) {
                                                            backgroundColor = lightestColor;
                                                         }
                                                         renderData.style += `
                                                         border-width: 2px;
                                                         border-color: ${selectedBarColor};
                                                         --event-background-color: ${backgroundColor};
                                                         background-color: ${backgroundColor};
                                                         color: ${getTextColorForBackground(
                                                            selectedBarColor,
                                                         )};
                                                      `;
                                                      } else if (
                                                         REQUEST_TASK_TYPES.has(entityType)
                                                      ) {
                                                         renderData.style += `
                                                         border-width: 2px;
                                                         border-color: ${selectedBarColor};
                                                         background-color: white;
                                                         border-style: dashed;
                                                         color: #667280;
                                                      `;
                                                      }
                                                   } else if (taskRecord.firstChild) {
                                                      const jobTitleColor =
                                                         taskRecord.firstChild.getData(
                                                            "jobTitleColor",
                                                         );
                                                      renderData.style += `
                                                      background-color: ${jobTitleColor};
                                                      color: ${getTextColorForBackground(
                                                         jobTitleColor,
                                                      )};
                                                   `;
                                                   }
                                                } else {
                                                   renderData.style += `
                                                   border-color: ${lightColor};
                                                   --event-background-color: ${lightestColor} !important;
                                                   --event-border-color: ${lightColor} !important;
                                                `;
                                                }

                                                // Takes care of rendering the task info on the task bar
                                                let textContent = "";

                                                if (isCategoryEntity) {
                                                   textContent = StringHelper.xss`${
                                                      taskRecord.name
                                                   } | ${formatDateBryntum(
                                                      taskRecord.startDate,
                                                   )} - ${formatDateBryntum(taskRecord.endDate)}`;
                                                } else if (isProjectEntity) {
                                                   const projectNumberStr = projectJobNumber
                                                      ? `(${projectJobNumber})`
                                                      : "";

                                                   textContent =
                                                      StringHelper.xss`${
                                                         taskRecord.name
                                                      } | ${projectNumberStr} ${formatDateBryntum(
                                                         taskRecord.startDate,
                                                      )} - ${formatDateBryntum(
                                                         taskRecord.endDate,
                                                      )}` + requestsNumberPill(requestsNumber);
                                                } else if (isTaskEntity || isSegment) {
                                                   const dailyStartTime =
                                                      parentTask.getData("startTime");
                                                   const dailyEndTime =
                                                      parentTask.getData("endTime");
                                                   const startTime = dailyStartTime
                                                      ? formatTimeBryntum(dailyStartTime)
                                                      : null;
                                                   const endTime = dailyEndTime
                                                      ? formatTimeBryntum(dailyEndTime)
                                                      : null;

                                                   textContent = StringHelper.xss`<span class="task-allocation-information">${getTaskTimeAllocation(
                                                      startTime,
                                                      endTime,
                                                      projectId,
                                                      id,
                                                   )}${!startTime && !endTime ? "%" : ""}</span>`;
                                                }

                                                if (isProjectEntity || isCategoryEntity)
                                                   if (
                                                      getGanttConfigurePanelValues()
                                                         .showProjectInformation === false
                                                   ) {
                                                      ganttRef.current?.instance?.element.classList.add(
                                                         "hide-project-information",
                                                      );
                                                   }

                                                if (
                                                   getGanttConfigurePanelValues()
                                                      .showAllocationInformation === false
                                                ) {
                                                   ganttRef.current?.instance?.element.classList.add(
                                                      "hide-allocation-information",
                                                   );
                                                }

                                                if (
                                                   getGanttConfigurePanelValues().showTimeoff ===
                                                   false
                                                ) {
                                                   ganttRef.current?.instance?.element.classList.add(
                                                      "hide-time-off-reasons",
                                                   );
                                                }

                                                if (
                                                   getGanttConfigurePanelValues().taskLabels ===
                                                   false
                                                ) {
                                                   ganttRef.current?.instance?.element.classList.add(
                                                      "hide-task-labels",
                                                   );
                                                }

                                                // TODO: add equipment requests?
                                                if (entityType === TaskType.EQUIPMENT_ASSIGNMENT) {
                                                   const excavatorIcon = renderToString(
                                                      <Excavator size="sm" color="#4f5964" />,
                                                   );
                                                   textContent = excavatorIcon + textContent;
                                                }

                                                return textContent;
                                             }}
                                             taskDragFeature={taskDragFeature}
                                             taskSegmentDragFeature={taskDragFeature}
                                             taskSegmentResizeFeature={taskResizeFeature}
                                             onGridRowBeforeDragStart={() => {
                                                if (!droppingGridRow) droppingGridRow = true;
                                             }}
                                             onGridRowBeforeDropFinalize={
                                                onGridRowBeforeDropFinalize
                                             }
                                             onGridRowDrop={() => {
                                                if (droppingGridRow) droppingGridRow = false;
                                             }}
                                             onBeforeTaskDropFinalize={async ({ context }) => {
                                                const droppedTasks: TaskModel[] = [];
                                                const daysDelta =
                                                   (context as any).timeDiff / 1000 / 60 / 60 / 24;

                                                context.taskRecords.forEach((drop: any) => {
                                                   // It's important that we clone here because if we don't, then the "cancel" behavior won't work properly in our
                                                   // procore conflict model (ie. the task will dissapear instead of simply canceling back to its previous location)
                                                   const droppedTask = drop.copy();
                                                   droppedTask.id = drop.id;

                                                   const startDate = new Date(
                                                      droppedTask.startDate,
                                                   );
                                                   startDate.setDate(
                                                      startDate.getDate() + daysDelta,
                                                   );
                                                   droppedTask.startDate = startDate;

                                                   const endDate = new Date(droppedTask.endDate);
                                                   endDate.setDate(endDate.getDate() + daysDelta);
                                                   droppedTask.endDate = endDate;

                                                   droppedTasks.push(droppedTask);
                                                });

                                                await taskSchedulingConflictHandler(
                                                   context,
                                                   droppedTasks,
                                                );
                                             }}
                                             onBeforeTaskSegmentDropFinalize={async ({
                                                context,
                                             }) => {
                                                const droppedTasks: TaskModel[] = [];
                                                const daysDelta =
                                                   (context as any).timeDiff / 1000 / 60 / 60 / 24;

                                                context.taskRecords.forEach((drop: any) => {
                                                   // It's important that we clone here because if we don't, then the "cancel" behavior won't work properly in our
                                                   // procore conflict model (ie. the task will dissapear instead of simply canceling back to its previous location)
                                                   const droppedTask = drop.copy();
                                                   droppedTask.id = drop.id;

                                                   const startDate = new Date(
                                                      droppedTask.startDate,
                                                   );
                                                   startDate.setDate(
                                                      startDate.getDate() + daysDelta,
                                                   );
                                                   droppedTask.startDate = startDate;

                                                   const endDate = new Date(droppedTask.endDate);
                                                   endDate.setDate(endDate.getDate() + daysDelta);
                                                   droppedTask.endDate = endDate;

                                                   droppedTasks.push(droppedTask);
                                                });

                                                await taskSchedulingConflictHandler(
                                                   context,
                                                   droppedTasks,
                                                );
                                             }}
                                             onBeforeTaskResizeFinalize={async ({ context }) => {
                                                const resizedTasks: TaskModel[] = [];
                                                context.resizeData.forEach((resize: any) => {
                                                   // It's important that we clone here because if we don't, then the "cancel" behavior won't work properly in our
                                                   // procore conflict model (ie. the task will dissapear instead of simply canceling back to its previous size)
                                                   const resizedTask = resize.taskRecord.copy();
                                                   resizedTask.id = resize.taskRecord.id;
                                                   // Remove 1ms from the end date to make sure that if it's midnight, the conflict checker doesn't round up
                                                   // thinking the assignment ends one day later than it actually does. For some reason we were having this issue
                                                   // with resizing but not with drag-and-dropping.
                                                   resizedTask.endDate = new Date(
                                                      resize.endDate.getTime() - 1,
                                                   );
                                                   resizedTasks.push(resizedTask);
                                                });

                                                await taskSchedulingConflictHandler(
                                                   context,
                                                   resizedTasks,
                                                );
                                             }}
                                             onBeforeTaskSegmentResizeFinalize={async (stuff) => {
                                                const resizedTasks: TaskModel[] = [];
                                                const context = stuff.context as any;
                                                context.resizeData.forEach((resize: any) => {
                                                   // It's important that we clone here because if we don't, then the "cancel" behavior won't work properly in our
                                                   // procore conflict model (ie. the task will dissapear instead of simply canceling back to its previous size)
                                                   const resizedTask = resize.taskRecord.copy();
                                                   resizedTask.id = resize.taskRecord.id;
                                                   // Remove 1ms from the end date to make sure that if it's midnight, the conflict checker doesn't round up
                                                   // thinking the assignment ends one day later than it actually does. For some reason we were having this issue
                                                   // with resizing but not with drag-and-dropping.
                                                   resizedTask.endDate = new Date(
                                                      resize.endDate.getTime() - 1,
                                                   );
                                                   resizedTasks.push(resizedTask);
                                                });

                                                await taskSchedulingConflictHandler(
                                                   context,
                                                   resizedTasks,
                                                );
                                             }}
                                             onCellDblClick={({ record }) => {
                                                if (
                                                   ![
                                                      TaskType.CATEGORY,
                                                      TaskType.SUBCATEGORY,
                                                   ].includes(record.getData("type"))
                                                )
                                                   return false;
                                             }}
                                             onTaskResizeEnd={({
                                                taskRecord,
                                             }: {
                                                taskRecord: TaskModel;
                                             }) => {
                                                subtractOneMinuteFromTaskEndDate(taskRecord);
                                             }}
                                             onTaskSegmentResizeEnd={({
                                                taskRecord,
                                             }: {
                                                taskRecord: TaskModel;
                                             }) => {
                                                subtractOneMinuteFromTaskEndDate(taskRecord);
                                             }}
                                             onTaskSegmentDrop={({
                                                taskRecords,
                                             }: {
                                                taskRecords: TaskModel[];
                                             }) => {
                                                taskRecords.forEach((taskRecord: TaskModel) => {
                                                   subtractOneMinuteFromTaskEndDate(taskRecord);
                                                });
                                             }}
                                             onTaskDrop={({
                                                taskRecords,
                                             }: {
                                                taskRecords: TaskModel[];
                                             }) => {
                                                taskRecords.forEach((taskRecord: TaskModel) => {
                                                   subtractOneMinuteFromTaskEndDate(taskRecord);
                                                });
                                             }}
                                             onTaskMenuBeforeShow={({
                                                taskRecord: menuTaskRecord,
                                                items,
                                             }: {
                                                taskRecord: any;
                                                items: any;
                                             }) => {
                                                const type = menuTaskRecord.getData("type");

                                                const createGroupingMenuOption = (items as any)
                                                   .createAssignmentGrouping as MenuItemConfig;
                                                const deleteGroupingMenuOption = (items as any)
                                                   .deleteAssignmentGrouping as MenuItemConfig;
                                                const createAssignmentMenuOption = (items as any)
                                                   .createAssignment as MenuItemConfig;
                                                const deleteAssignmentMenuOption = (items as any)
                                                   .deleteAssignment as MenuItemConfig;
                                                const editAssignmentMenuOption = (items as any)
                                                   .editAssignment as MenuItemConfig;
                                                const createRequestMenuOption = (items as any)
                                                   .createRequest as MenuItemConfig;
                                                const deleteRequestMenuOption = (items as any)
                                                   .deleteRequest as MenuItemConfig;
                                                const editRequestMenuOption = (items as any)
                                                   .editRequest as MenuItemConfig;
                                                const unassignMenuOption = (items as any)
                                                   .unassign as MenuItemConfig;
                                                const sendAssignmentAlertsMenuOption = (
                                                   items as any
                                                ).sendAssignmentAlerts as MenuItemConfig;
                                                const splitTaskMenuOption =
                                                   (items as any).splitTask ??
                                                   ({} as MenuItemConfig);

                                                createAssignmentMenuOption.hidden = true;
                                                createRequestMenuOption.hidden = true;
                                                createGroupingMenuOption.hidden = true;
                                                editAssignmentMenuOption.hidden = true;
                                                editRequestMenuOption.hidden = true;
                                                unassignMenuOption.hidden = true;
                                                sendAssignmentAlertsMenuOption.hidden = true;
                                                deleteGroupingMenuOption.hidden = true;
                                                deleteAssignmentMenuOption.hidden = true;
                                                deleteRequestMenuOption.hidden = true;
                                                splitTaskMenuOption.hidden = true;

                                                if (type === TaskType.PROJECT) {
                                                   createGroupingMenuOption.hidden = false;
                                                   createAssignmentMenuOption.hidden = false;
                                                   createRequestMenuOption.hidden = false;
                                                   sendAssignmentAlertsMenuOption.hidden = false;
                                                } else if (type === TaskType.CATEGORY) {
                                                   createGroupingMenuOption.hidden = false;
                                                   deleteGroupingMenuOption.hidden = false;
                                                   createAssignmentMenuOption.hidden = false;
                                                   createRequestMenuOption.hidden = false;
                                                } else if (type === TaskType.SUBCATEGORY) {
                                                   deleteGroupingMenuOption.hidden = false;
                                                   createAssignmentMenuOption.hidden = false;
                                                   createRequestMenuOption.hidden = false;
                                                } else if (ASSIGNMENT_TASK_TYPES.has(type)) {
                                                   if (
                                                      !checkAuthAction(
                                                         AuthAction.MANAGE_ASSIGNMENTS,
                                                      )
                                                   )
                                                      return false;
                                                   editAssignmentMenuOption.hidden = false;
                                                   deleteAssignmentMenuOption.hidden = false;
                                                } else if (REQUEST_TASK_TYPES.has(type)) {
                                                   if (!checkAuthAction(AuthAction.MANAGE_REQUESTS))
                                                      return false;
                                                   editRequestMenuOption.hidden = false;
                                                   deleteRequestMenuOption.hidden = false;
                                                }
                                                if (type === TaskType.ASSIGNMENT) {
                                                   unassignMenuOption.hidden = false;
                                                   if (
                                                      LaunchDarklyClient.getFlagValue(
                                                         "gantt-split-task",
                                                      )
                                                   ) {
                                                      splitTaskMenuOption.hidden = false; // only enable this for resource assignments right now. we will support equipment splitting in future
                                                   }
                                                }
                                                if (
                                                   !checkAuthAction(AuthAction.MANAGE_ASSIGNMENTS)
                                                ) {
                                                   createAssignmentMenuOption.hidden = true;
                                                   editAssignmentMenuOption.hidden = true;
                                                   deleteAssignmentMenuOption.hidden = true;
                                                   unassignMenuOption.hidden = true;
                                                   splitTaskMenuOption.hidden = true;
                                                }
                                                if (!checkAuthAction(AuthAction.MANAGE_REQUESTS)) {
                                                   createRequestMenuOption.hidden = true;
                                                   editRequestMenuOption.hidden = true;
                                                   deleteRequestMenuOption.hidden = true;
                                                   unassignMenuOption.hidden = true;
                                                }
                                                if (!checkAuthAction(AuthAction.MANAGE_ALERTS)) {
                                                   sendAssignmentAlertsMenuOption.hidden = true;
                                                }

                                                if (type === TaskType.PROJECT) {
                                                   createGroupingMenuOption.text = I18n.t(
                                                      "views.company.workforce_planning.gantt.task_menu.add_category",
                                                   );
                                                   createGroupingMenuOption.onItem = async (event: {
                                                      taskRecord?: any;
                                                   }) => {
                                                      const { taskRecord } = event;
                                                      if (!taskRecord) return;
                                                      const newId = new Model()
                                                         .generateId()
                                                         .split("_")
                                                         .at(-1);
                                                      const newCategory = {
                                                         id: newId,
                                                         name: "New Category",
                                                         type: "category",
                                                         projectId: taskRecord.getData("id"),
                                                         expanded: true,
                                                         sortPriority:
                                                            sortPriorityLevels[TaskType.CATEGORY],
                                                      };

                                                      const newTask =
                                                         await ganttRef.current!.instance.addSubtask(
                                                            taskRecord as TaskModel,
                                                            {
                                                               data: {
                                                                  ...newCategory,
                                                               },
                                                            },
                                                         );

                                                      expandTask(newTask.get("id"));

                                                      ganttRef.current!.instance.features.cellEdit.startEditing(
                                                         {
                                                            record: newTask,
                                                            field: "name",
                                                         },
                                                      );
                                                   };

                                                   // only show 'send assignmnet alerts' option for non-empty projects
                                                   if (
                                                      menuTaskRecord.allChildren.filter(
                                                         (child: any) =>
                                                            ["assignment", "request"].includes(
                                                               child.getData("type"),
                                                            ),
                                                      ).length === 0
                                                   ) {
                                                      sendAssignmentAlertsMenuOption.hidden = true;
                                                   }
                                                } else if (type === TaskType.CATEGORY) {
                                                   createGroupingMenuOption.text = I18n.t(
                                                      "views.company.workforce_planning.gantt.task_menu.add_subcategory",
                                                   );
                                                   createGroupingMenuOption.onItem = async (event: {
                                                      taskRecord?: any;
                                                   }) => {
                                                      const { taskRecord } = event;
                                                      if (!taskRecord) return;
                                                      const projectId =
                                                         taskRecord.getData("projectId");
                                                      const categoryId = taskRecord.getData("id");
                                                      const newId = new Model()
                                                         .generateId()
                                                         .split("_")
                                                         .at(-1);

                                                      const newSubcategory = {
                                                         id: newId,
                                                         name: "New Subcategory",
                                                         text: "New Subcategory",
                                                         type: "subcategory",
                                                         projectId,
                                                         categoryId,
                                                         expanded: true,
                                                         sortPriority:
                                                            sortPriorityLevels[
                                                               TaskType.SUBCATEGORY
                                                            ],
                                                      };

                                                      const newTask =
                                                         await ganttRef.current!.instance.addSubtask(
                                                            taskRecord as TaskModel,
                                                            {
                                                               data: {
                                                                  ...newSubcategory,
                                                               },
                                                            },
                                                         );

                                                      expandTask(newTask.get("id"));

                                                      ganttRef.current!.instance.features.cellEdit.startEditing(
                                                         {
                                                            record: newTask,
                                                            field: "name",
                                                         },
                                                      );
                                                   };

                                                   deleteGroupingMenuOption.text = I18n.t(
                                                      "views.company.workforce_planning.gantt.task_menu.delete_category",
                                                   );
                                                   deleteGroupingMenuOption.onItem = ({
                                                      taskRecord,
                                                   }) => {
                                                      if (!taskRecord) taskRecord = menuTaskRecord;

                                                      showBryntumModal({
                                                         type: BryntumModalType.DeleteConfirmation,
                                                         modalId:
                                                            "gantt-confirmation-modal-container",
                                                         title: "Delete Category?",
                                                         body: (
                                                            <P>
                                                               <span>
                                                                  Are you sure you want to
                                                                  permanently delete this category?
                                                                  Doing so will remove all
                                                                  assignments and requests within
                                                                  the category.{" "}
                                                               </span>
                                                               <b style={{ fontWeight: "600" }}>
                                                                  This action cannot be undone.
                                                               </b>
                                                            </P>
                                                         ),
                                                         onSave: async () => {
                                                            if (taskRecord)
                                                               ganttRef.current?.instance.taskStore.remove(
                                                                  taskRecord,
                                                               );
                                                         },
                                                      });
                                                   };
                                                } else if (type === TaskType.SUBCATEGORY) {
                                                   deleteGroupingMenuOption.text = I18n.t(
                                                      "views.company.workforce_planning.gantt.task_menu.delete_subcategory",
                                                   );
                                                   deleteGroupingMenuOption.onItem = ({
                                                      taskRecord,
                                                   }) => {
                                                      if (!taskRecord) taskRecord = menuTaskRecord;

                                                      showBryntumModal({
                                                         type: BryntumModalType.DeleteConfirmation,
                                                         modalId:
                                                            "gantt-confirmation-modal-container",
                                                         title: "Delete Subcategory?",
                                                         body: (
                                                            <P>
                                                               <span>
                                                                  Are you sure you want to
                                                                  permanently delete this
                                                                  subcategory? Doing so will remove
                                                                  all assignments and requests
                                                                  within the subcategory.{" "}
                                                               </span>
                                                               <b style={{ fontWeight: "600" }}>
                                                                  This action cannot be undone.
                                                               </b>
                                                            </P>
                                                         ),
                                                         onSave: () => {
                                                            ganttRef.current?.instance.taskStore.remove(
                                                               taskRecord as TaskModel,
                                                            );
                                                         },
                                                      });
                                                   };
                                                }

                                                createAssignmentMenuOption.onItem = ({
                                                   taskRecord,
                                                }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   const projectId =
                                                      type === TaskType.PROJECT
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("projectId");
                                                   const categoryId =
                                                      type === TaskType.CATEGORY
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("categoryId");
                                                   const subcategoryId =
                                                      type === TaskType.SUBCATEGORY
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("subcategoryId");

                                                   createAssignmentTearsheetRef.current?.handleOpenTearsheet(
                                                      {
                                                         projectId,
                                                         categoryId,
                                                         subcategoryId,
                                                         callback: async ({
                                                            newTask,
                                                            calendarForNewTask,
                                                         }) => {
                                                            const ganttInstance: any =
                                                               ganttRef.current?.instance;

                                                            addCalendarIfNotExists(
                                                               calendarForNewTask,
                                                            );
                                                            await ganttInstance.crudManager.suspendAutoSync();

                                                            const parentTask: TaskModel =
                                                               ganttInstance.taskStore.getById(
                                                                  newTask.subcategoryId ??
                                                                     newTask.categoryId,
                                                               ) ?? taskRecord;

                                                            const newTaskRecord =
                                                               await ganttRef.current?.instance.addSubtask(
                                                                  parentTask,
                                                                  {
                                                                     data: newTask,
                                                                  },
                                                               );
                                                            // We're explicitly updating endDate here because for some reason, Bryntum isn't setting correct endDate on addSubtask
                                                            if (newTaskRecord)
                                                               await newTaskRecord.setEndDate(
                                                                  newTask.endDate,
                                                               );

                                                            await ganttInstance.crudManager.clearChanges();
                                                            await ganttInstance.crudManager.resumeAutoSync();

                                                            if (
                                                               !ganttInstance.taskStore.find(
                                                                  (task: any) =>
                                                                     task.id === newTask.id,
                                                               )
                                                            )
                                                               ganttInstance.taskStore.add(newTask);
                                                         },
                                                      },
                                                   );
                                                };

                                                editAssignmentMenuOption.onItem = (event: {
                                                   item: any;
                                                   menu: any;
                                                   domEvent: any;
                                                   taskRecord?: any;
                                                }) => {
                                                   const { taskRecord, domEvent } = event;
                                                   const resourceType =
                                                      type === TaskType.ASSIGNMENT
                                                         ? ResourceType.LABOR
                                                         : ResourceType.EQUIPMENT;
                                                   if (!taskRecord || !domEvent) return;
                                                   const projectId =
                                                      type === TaskType.PROJECT
                                                         ? taskRecord.getData("id")
                                                         : taskRecord.getData("projectId");
                                                   const categoryId =
                                                      type === TaskType.CATEGORY
                                                         ? taskRecord.getData("id")
                                                         : taskRecord.getData("categoryId");
                                                   const subcategoryId =
                                                      type === TaskType.SUBCATEGORY
                                                         ? taskRecord.getData("id")
                                                         : taskRecord.getData("subcategoryId");

                                                   const recordToEdit = taskRecord;

                                                   editAssignmentTearsheetRef.current?.handleOpenTearsheet(
                                                      {
                                                         assignmentId: recordToEdit.getData("id"),
                                                         projectId,
                                                         categoryId,
                                                         subcategoryId,
                                                         callback: async ({
                                                            newTask,
                                                            calendarForNewTask,
                                                         }: {
                                                            newTask: any;
                                                            calendarForNewTask: any;
                                                         }) => {
                                                            await suspendAutoSync(ganttRef);

                                                            addCalendarIfNotExists(
                                                               calendarForNewTask,
                                                            );

                                                            newTask.calendar =
                                                               calendarForNewTask.name;

                                                            const taskFromStore = taskStore.getById(
                                                               taskRecord?.id ?? "",
                                                            );
                                                            const parentProject =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  newTask.projectId,
                                                               );
                                                            const parentTask: TaskModel =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  newTask.subcategoryId ??
                                                                     newTask.categoryId,
                                                               ) ?? (parentProject as any);

                                                            if (taskFromStore) {
                                                               if (
                                                                  taskFromStore.parentId !==
                                                                  parentTask.id
                                                               ) {
                                                                  await ganttRef.current?.instance.taskStore.remove(
                                                                     taskFromStore,
                                                                  );
                                                                  taskFromStore.set(newTask);
                                                                  await ganttRef.current?.instance.addSubtask(
                                                                     parentTask,
                                                                     {
                                                                        data: newTask,
                                                                     },
                                                                  );
                                                               } else {
                                                                  taskFromStore.set(newTask);
                                                               }
                                                            }

                                                            await clearChanges(ganttRef);
                                                            await resumeAutoSync(ganttRef);
                                                         },
                                                         resourceType,
                                                      },
                                                   );
                                                };

                                                sendAssignmentAlertsMenuOption.onItem = ({
                                                   taskRecord,
                                                }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   const projectId =
                                                      type === TaskType.PROJECT
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("projectId");

                                                   sendAssignmentAlertsTearsheetRef.current?.handleOpenTearsheet(
                                                      {
                                                         projectId,
                                                      },
                                                   );
                                                };

                                                deleteAssignmentMenuOption.onItem = ({
                                                   taskRecord,
                                                }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   showBryntumModal({
                                                      type: BryntumModalType.DeleteConfirmation,
                                                      modalId: "gantt-confirmation-modal-container",
                                                      title: "Delete Assignment?",
                                                      body: (
                                                         <P>
                                                            <span>
                                                               Are you sure you want to permanently
                                                               delete this assignment?{" "}
                                                            </span>
                                                            <b style={{ fontWeight: "600" }}>
                                                               This action cannot be undone.
                                                            </b>
                                                         </P>
                                                      ),
                                                      onSave: async () => {
                                                         if (!taskRecord) return;

                                                         ganttRef.current?.instance.taskStore.remove(
                                                            taskRecord,
                                                         );
                                                      },
                                                   });
                                                };

                                                createRequestMenuOption.onItem = ({
                                                   taskRecord,
                                                }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   const projectId =
                                                      type === TaskType.PROJECT
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("projectId");
                                                   const categoryId =
                                                      type === TaskType.CATEGORY
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("categoryId");
                                                   const subcategoryId =
                                                      type === TaskType.SUBCATEGORY
                                                         ? taskRecord!.getData("id")
                                                         : taskRecord!.getData("subcategoryId");

                                                   createRequestTearsheetRef.current?.handleOpenTearsheet(
                                                      {
                                                         projectId,
                                                         categoryId,
                                                         subcategoryId,
                                                         callback: async ({ newTasks }: any) => {
                                                            await (
                                                               ganttRef.current?.instance as any
                                                            ).crudManager.suspendAutoSync();

                                                            const projectId = newTasks[0].projectId;

                                                            for (const newTask of newTasks) {
                                                               const parentTask: TaskModel =
                                                                  ganttRef.current?.instance.taskStore.getById(
                                                                     newTask.subcategoryId ??
                                                                        newTask.categoryId,
                                                                  ) ?? (taskRecord as any);

                                                               const newTaskRecord =
                                                                  await ganttRef.current?.instance.addSubtask(
                                                                     parentTask, // Parent task (Project, Category, or Subcategory)
                                                                     {
                                                                        data: newTask,
                                                                     },
                                                                  );
                                                               // Using this method to update the endDate because is updating the schedule otherwise the endDate change is not reflected
                                                               await newTaskRecord?.setEndDate(
                                                                  newTask.endDate,
                                                               );
                                                            }

                                                            await (
                                                               ganttRef.current?.instance as any
                                                            ).crudManager.clearChanges();
                                                            await (
                                                               ganttRef.current?.instance as any
                                                            ).crudManager.resumeAutoSync();

                                                            const project =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  projectId,
                                                               );
                                                            project?.set(
                                                               "requestsNumber",
                                                               (project?.get("requestsNumber") ??
                                                                  0) + newTasks.length,
                                                            );
                                                         },
                                                      },
                                                   );
                                                };

                                                editRequestMenuOption.onItem = ({ taskRecord }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   const resourceType =
                                                      type === TaskType.REQUEST
                                                         ? ResourceType.LABOR
                                                         : ResourceType.EQUIPMENT;
                                                   const projectId =
                                                      taskRecord!.getData("projectId");
                                                   const categoryId =
                                                      taskRecord!.getData("categoryId");
                                                   const subcategoryId =
                                                      taskRecord!.getData("subcategoryId");

                                                   editRequestTearsheetRef.current?.handleOpenTearsheet(
                                                      {
                                                         requestId: taskRecord!.getData("id"),
                                                         projectId,
                                                         categoryId,
                                                         subcategoryId,
                                                         callback: async ({ newTasks }: any) => {
                                                            const newTask = newTasks[0];
                                                            await (
                                                               ganttRef.current?.instance as any
                                                            ).crudManager.suspendAutoSync();

                                                            const taskFromStore =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  taskRecord?.id ?? "",
                                                               );
                                                            const parentProject =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  newTask.projectId,
                                                               );
                                                            const parentTask: TaskModel =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  newTask.subcategoryId ??
                                                                     newTask.categoryId,
                                                               ) ?? (parentProject as any);

                                                            if (taskFromStore) {
                                                               if (
                                                                  taskFromStore.parentId !==
                                                                  parentTask.id
                                                               ) {
                                                                  await ganttRef.current?.instance.taskStore.remove(
                                                                     taskFromStore,
                                                                  );
                                                                  taskFromStore.set(newTask);
                                                                  await ganttRef.current?.instance.addSubtask(
                                                                     parentTask,
                                                                     {
                                                                        data: newTask,
                                                                     },
                                                                  );
                                                               } else {
                                                                  taskFromStore.set(newTask);
                                                               }
                                                            }

                                                            await clearChanges(ganttRef);
                                                            await resumeAutoSync(ganttRef);
                                                         },
                                                         resourceType,
                                                      },
                                                   );
                                                };

                                                unassignMenuOption.onItem = async ({
                                                   taskRecord,
                                                }) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   const projectId =
                                                      taskRecord!.getData("projectId");
                                                   const categoryId =
                                                      taskRecord!.getData("categoryId");
                                                   const subcategoryId =
                                                      taskRecord!.getData("subcategoryId");
                                                   const parentProject =
                                                      ganttRef.current?.instance.taskStore.getById(
                                                         projectId,
                                                      );

                                                   await (
                                                      ganttRef.current?.instance as any
                                                   ).crudManager.suspendAutoSync();

                                                   /*
                                                /  1. Try to create request in database
                                                /  2. Try to delete assignment from database
                                                /  3. If successful with both:
                                                /     - remove the assignment task from the gantt
                                                /     - add the request task to the gantt
                                                /     - generate alert that user was unnassigned
                                                */
                                                   try {
                                                      const taskData: UnassignTaskData = {
                                                         id: taskRecord!.getData("id"),
                                                         startDate:
                                                            taskRecord!.getData("startDate"),
                                                         endDate: taskRecord!.getData("endDate"),
                                                         startTime:
                                                            taskRecord!.getData("startTime"),
                                                         endTime: taskRecord!.getData("endTime"),
                                                         jobTitleId:
                                                            taskRecord!.getData("jobTitleId"),
                                                         projectId,
                                                         workDays: taskRecord!.getData("workDays"),
                                                         categoryId,
                                                         instructionText:
                                                            taskRecord!.getData("instructionText"),
                                                         percentAllocated:
                                                            taskRecord!.getData("percentAllocated"),
                                                         statusId: taskRecord!.getData("statusId"),
                                                         subcategoryId,
                                                         tagIds: taskRecord!.getData("tagIds"),
                                                         workScopeText:
                                                            taskRecord!.getData("workScopeText"),
                                                      };

                                                      const result = await handleUnassignTask(
                                                         taskData,
                                                      );

                                                      (taskRecord as any).id = result.newRequestId;
                                                      (taskRecord as any).name = result.taskName;
                                                      (taskRecord as any).type = result.taskType;

                                                      parentProject?.set(
                                                         "requestsNumber",
                                                         (parentProject?.get("requestsNumber") ??
                                                            0) + 1,
                                                      );
                                                   } catch (error) {
                                                      console.error(error);
                                                   }

                                                   await (
                                                      ganttRef.current?.instance as any
                                                   ).crudManager.clearChanges();
                                                   await (
                                                      ganttRef.current?.instance as any
                                                   ).crudManager.resumeAutoSync();
                                                };

                                                deleteRequestMenuOption.onItem = ({
                                                   taskRecord,
                                                }: any) => {
                                                   if (!taskRecord) taskRecord = menuTaskRecord;

                                                   showBryntumModal({
                                                      type: BryntumModalType.DeleteConfirmation,
                                                      modalId: "gantt-confirmation-modal-container",
                                                      title: "Delete Request?",
                                                      body: (
                                                         <P>
                                                            <span>
                                                               Are you sure you want to permanently
                                                               delete this request?{" "}
                                                            </span>
                                                            <b style={{ fontWeight: "600" }}>
                                                               This action cannot be undone.
                                                            </b>
                                                         </P>
                                                      ),
                                                      onSave: async () => {
                                                         if (taskRecord) {
                                                            ganttRef.current?.instance.taskStore.remove(
                                                               taskRecord,
                                                            );

                                                            const project =
                                                               ganttRef.current?.instance.taskStore.getById(
                                                                  taskRecord.getData("projectId"),
                                                               );
                                                            project?.set(
                                                               "requestsNumber",
                                                               (project?.get("requestsNumber") ??
                                                                  0) - 1,
                                                            );
                                                         }
                                                      },
                                                   });
                                                };

                                                return true;
                                             }}
                                          />
                                       </>
                                    )
                                 )}
                              </Spinner>
                           </Box>
                        </Box>
                     </SplitViewCard.Section>
                  </SplitViewCard.Main>
               </SplitViewCard>
               <GanttProjectDetailsTearsheet ref={projectDetailsTearsheetRef} />
               <GanttPersonDetailsTearsheet ref={personDetailsTearsheetRef} />
               <GanttCreateAssignmentTearsheet ref={createAssignmentTearsheetRef} />
               <GanttSendOneOffAssignmentAlertsTearsheet ref={sendAssignmentAlertsTearsheetRef} />
               <GanttCreateRequestTearsheet ref={createRequestTearsheetRef} />
               <GanttEditAssignmentTearsheet ref={editAssignmentTearsheetRef} />
               <GanttEditRequestTearsheet ref={editRequestTearsheetRef} />
            </ToolLandingPage.Body>
         </ToolLandingPage.Main>
      </ToolLandingPage>
   );
};
